import React, { Component } from "react";
import { post } from "aws-amplify/api";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import {
  getCustomFees,
  getManagementFees,
  getManagementFeeTiers,
  getS3File,
} from "../../services/submissionService";
import CollapsibleSection from "./CollapsibleSection";
import ISPClientProfile from "./ispClientProfile";
import ISPAccountInfo from "./ispAccountInfo";
import RiskQuestionnaire, {
  CAPACITY_QUESTIONS,
  WILLINGNESS_QUESTIONS,
  getPortfolioRiskCategory,
  portfolioMatchesRiskCategory,
} from "./RiskQuestionnaire";
import ISPFeeSchedule from "./ispFeeSchedule";
import ISPConfirm from "./ispConfirm";
import { getFusionFeeValidation, sortFeeTiers, sortManagementFeeTiers } from "./fusionFeeUtils";
import {
  calculateFusionWeightedRiskScore,
  getEligibleFusionStrategies,
  getFusionAllocationDisplayName,
  getProgramDisplayName,
  isStrategyAvailableForAdvisors,
} from "../../utils/proposalUtils";
import {
  buildManagementFeeTierOptions,
  collapseTierLabel,
  normalizeTierKey,
  resolveManagementFeeTierSelection,
  splitEvolutionTiers,
} from "./managementFeeTierUtils";
import { normalizeIarFeeTiered } from "./iarFeeTieredUtils";

// All strategies now use the same questions with max 100.
function computeRiskScores(capAnswers, willAnswers) {
  const capacityTotal = CAPACITY_QUESTIONS.reduce((sum, q) => sum + (capAnswers[q.id] || 0), 0);
  const willingnessTotal = WILLINGNESS_QUESTIONS.reduce((sum, q) => sum + (willAnswers[q.id] || 0), 0);
  const combinedScore = capacityTotal + willingnessTotal;
  const riskCategory = combinedScore <= 35 ? "Conservative" : combinedScore <= 65 ? "Moderate" : "Growth";
  return { capacityTotal, willingnessTotal, combinedScore, riskCategory };
}

// Normalize either an ISO date (YYYY-MM-DD) or a legacy localized M/D/YYYY
// (left over from earlier submissions that were saved in display format) back
// to the YYYY-MM-DD shape that <input type="date"> requires. Anything we can't
// parse is dropped to "" so it can't crash the date input's min/max math.
function toIsoDate(dateStr) {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const m = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const mm = m[1].padStart(2, "0");
    const dd = m[2].padStart(2, "0");
    return `${m[3]}-${mm}-${dd}`;
  }
  return "";
}

// Narrow a list of portfolio names to those matching a chosen model selection.
// Convention (shared with getAvailableBondTypesForModel): the model option name
// minus a trailing " Strategy" is a prefix of — or equal to — the portfolio
// name (e.g. "Core Bond Strategy" matches "Core Bond - Taxable"). With no model
// selected the full list passes through unchanged.
function narrowBondsByModel(bonds, modelSelection) {
  const key = (modelSelection || "").replace(/\s+Strategy$/i, "").trim().toLowerCase();
  if (!key) return bonds;
  return bonds.filter((b) => {
    const lower = b.toLowerCase();
    return (
      lower === key ||
      lower.startsWith(`${key} `) ||
      lower.startsWith(`${key}-`) ||
      lower.startsWith(`${key} -`)
    );
  });
}

function getEmptySoloKQuestionnaire() {
  return {
    sponsoringBusinessName: "",
    ownerCount: "",
    employeeCount: "",
    anticipatesEmployees: "",
    planType: "",
    existingPlanDocumentsProvided: false,
    ownershipEvidence: [],
    certificationNoEmployees: false,
    certificationContributionLimits: false,
  };
}

function isSoloKRegistrationValue(value) {
  const raw = String(value || "").toLowerCase();
  return (
    raw.includes("solo(k)") ||
    raw.includes("solo k") ||
    raw.includes("solo 401")
  );
}

async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    if (session.tokens?.idToken) {
      return { Authorization: `Bearer ${session.tokens.idToken.toString()}` };
    }
    return {};
  } catch (error) {
    console.log("Error getting auth headers:", error);
    return {};
  }
}

class ISPForm extends Component {
  state = {
    // Section expansion
    expandedSection: "1",

    // Section 1: Client Information
    advisorName: "",
    additionalAdvisors: [], // array of name strings for secondary advisors
    requestType: "",
    existingRequestAction: "",
    accountNumber: "",
    accountNumbers: [""],
    // T1 — round 4: advisor attests whether a Client Profile + IMA is being
    // submitted alongside this ISP. "No" surfaces a long attestation paragraph
    // on the PDF (built by api-isp-request.py) just above the signature block.
    clientProfileImaSubmitted: "",
    householding: "",
    // T8 — round 4: householding reference split into separate name + last-4
    // inputs (both required when householding is Yes). The legacy single
    // householdRef field is preserved as a fallback so older drafts/submissions
    // restored from sessionStorage don't blow up.
    householdRefName: "",
    householdRefLast4: "",
    householdRef: "",
    householdReason: "",
    householdReasonType: "",
    householdReasonOther: "",
    jointClients: [],
    showClientDialog: false,
    dialogEditIndex: -1,
    dialogCopySource: -1,

    // Section 2: Account Information
    custodian: "",
    strategy: "",
    bondType: "Taxable",
    modelSelection: "",
    registrations: [
      { registration: "registration0", value: "", percentage: "100" },
    ],
    initialInvestmentAmount: "",
    accountNetWorthPct: "",
    accountIncomeNeed: "",
    accountIncomeAmount: "",
    accountIncomeStartDate: "",
    accountIncomeEndDate: "",
    accountIncomeFrequency: "",
    accountGoal: "",
    accountGoalOther: "",
    sourceOfFunds: [],
    sourceOfFundsText: {},
    soloKQuestionnaire: getEmptySoloKQuestionnaire(),
    // T15 — round 4: when registrations[0] is a UGMA/UTMA registration type,
    // capture the minor's name and their relationship to the custodian.
    // Cleared when the registration changes to a non-UGMA/UTMA type.
    minorName: "",
    relationshipToCustodian: "",
    businessName: "",
    businessEinLast4: "",
    fusionRegistrationType: "Single",
    fusionRebalanceType: "Automatic",
    // Path (graduation) strategy bond choice — independent of the
    // primary `bondType`. Only meaningful when the selected Elements strategy's
    // path target offers both Taxable and Municipal.
    pathBondType: "",

    // Section 3: Risk Questionnaire
    capacityAnswers: {},
    willingnessAnswers: {},
    capacityTotal: 0,
    willingnessTotal: 0,
    combinedScore: 0,
    riskCategory: "",
    selectedPortfolio: "",
    portfolioOverride: false,
    overrideReason: "",
    overrideOtherText: "",

    // Section 4: Fees
    customFees: null,
    managementFees: null,
    managementFeesLoading: false,
    iarFeeTier: "",
    selectedManagementFeeTierKey: "",
    availableManagementFeeTiers: [],
    availableManagementFeeTierOptions: [],
    iarFeeType: "",
    iarFeeFlat: "",
    iarFeeTiered: [],
    // Path-stage IAR fee (post-graduation). Used only when the selected
    // strategy is flat (`flatFee` on its mgmt-fees row) AND its pathStrategy
    // resolves to a real fee row. Mirrors iarFeeType/iarFeeFlat/iarFeeTiered.
    pathIarFeeType: "Fixed Percentage",
    pathIarFeeFlat: "",
    pathIarFeeTiered: [],
    iarFeeScheduleType: "Schedule 1",
    iarFeeWaiver: null, // { confirmedAt, confirmedBy, zeroFields }
    feesSectionVisited: false,

    // Fusion blended fee allocations
    fusionAllocations: [],
    fusionSubFees: {},
    fusionSubFeesLoading: false,
    fusionFeeValidation: { isValid: true, issues: [] },

    // Zero fee modal
    showZeroFeeModal: false,
    pendingSection: null, // section user wants to navigate to

    // Q5 chart image loaded from S3
    riskChartUrl: "",

    // Generic confirm modal
    confirmModal: null, // { title, message, onConfirm }

    // Section 5: Review & Submit
    isSubmitting: false,
    showSuccess: false,
    submitError: "",
    proposalPrefillInfo: "",
    isEditing: false,
  };

  _mounted = true;

  // Monotonic tokens guarding async fee fetches. A tier/strategy change can
  // leave an earlier fetch in flight; without these, a slow Standard-tier
  // response can land after the newer Tier B response and clobber state.
  _managementFeesRequestId = 0;
  _fusionSubFeesRequestId = 0;

  componentDidMount() {
    document.addEventListener("keydown", this.handleGlobalKeyDown);

    // Auto-set default advisor if lists are already loaded
    const defaultAdvisor = this.props.lists?.userInfo?.DefaultAdvisor;
    if (defaultAdvisor && !this.state.advisorName) {
      this.setState({ advisorName: defaultAdvisor });
    }

    // Edit mode takes priority over prefill — only one should run
    if (sessionStorage.getItem("editISPData")) {
      this.checkForEditData();
    } else {
      this.checkForProposalPrefill();
    }

    // Load Q5 risk chart image from S3
    this.loadRiskChart();
  }

  checkForEditData = () => {
    try {
      const raw = sessionStorage.getItem("editISPData");
      if (!raw) return;

      const editDateTime = sessionStorage.getItem("editISPDateTime") || "";
      sessionStorage.removeItem("editISPData");
      sessionStorage.removeItem("editISPDateTime");

      const data = JSON.parse(raw);
      const updates = {
        isEditing: !!editDateTime,
        proposalDateTime: editDateTime || undefined,
      };

      // Section 1: Client Information
      if (data.advisorName) updates.advisorName = data.advisorName;
      if (data.requestType) updates.requestType = data.requestType;
      if (data.existingRequestAction) updates.existingRequestAction = data.existingRequestAction;
      if (data.accountNumber) updates.accountNumber = data.accountNumber;
      if (data.accountNumbers && Array.isArray(data.accountNumbers)) updates.accountNumbers = data.accountNumbers;
      if (data.clientProfileImaSubmitted) updates.clientProfileImaSubmitted = data.clientProfileImaSubmitted;
      if (data.householding) updates.householding = data.householding;
      if (data.householdRefName) updates.householdRefName = data.householdRefName;
      if (data.householdRefLast4) updates.householdRefLast4 = data.householdRefLast4;
      // Older drafts only carry householdRef (the legacy combined last-4
      // field). Restore it as the new last-4 input so saved-but-unmigrated
      // forms don't drop the value silently.
      if (data.householdRef) {
        updates.householdRef = data.householdRef;
        if (!data.householdRefLast4) updates.householdRefLast4 = data.householdRef;
      }
      if (data.householdReason) updates.householdReason = data.householdReason;
      if (data.householdReasonType) {
        updates.householdReasonType = data.householdReasonType;
      } else if (data.householdReason === "This is the only account in the household.") {
        updates.householdReasonType = "Only account";
      } else if (data.householdReason) {
        updates.householdReasonType = "Other";
        updates.householdReasonOther = data.householdReason;
      }
      if (data.householdReasonOther) updates.householdReasonOther = data.householdReasonOther;
      if (data.jointClients && Array.isArray(data.jointClients)) updates.jointClients = data.jointClients;
      if (data.additionalAdvisors && Array.isArray(data.additionalAdvisors)) updates.additionalAdvisors = data.additionalAdvisors;

      // Section 2: Account Information
      if (data.custodian) updates.custodian = data.custodian;
      if (data.strategy) updates.strategy = data.strategy;
      if (data.bondType) updates.bondType = data.bondType;
      if (data.modelSelection) updates.modelSelection = data.modelSelection;
      if (data.fusionRegistrationType) {
        updates.fusionRegistrationType = data.fusionRegistrationType;
      } else if ((data.strategy || "").toLowerCase().includes("fusion")) {
        updates.fusionRegistrationType = "Single";
      }
      if (data.fusionRebalanceType) {
        updates.fusionRebalanceType = data.fusionRebalanceType;
      } else if ((data.strategy || "").toLowerCase().includes("fusion")) {
        updates.fusionRebalanceType =
          data.fusionRegistrationType === "Multi" ? "Manual" : "Automatic";
      }
      if (data.registrations && Array.isArray(data.registrations)) updates.registrations = data.registrations;
      if (data.initialInvestmentAmount) updates.initialInvestmentAmount = data.initialInvestmentAmount;
      if (data.accountNetWorthPct) updates.accountNetWorthPct = data.accountNetWorthPct;
      if (data.accountIncomeNeed) updates.accountIncomeNeed = data.accountIncomeNeed;
      if (data.accountIncomeAmount) updates.accountIncomeAmount = data.accountIncomeAmount;
      if (data.accountIncomeStartDate) updates.accountIncomeStartDate = toIsoDate(data.accountIncomeStartDate);
      if (data.accountIncomeEndDate) updates.accountIncomeEndDate = toIsoDate(data.accountIncomeEndDate);
      if (data.accountIncomeFrequency) updates.accountIncomeFrequency = data.accountIncomeFrequency;
      if (data.accountGoal) updates.accountGoal = data.accountGoal;
      if (data.accountGoalOther) updates.accountGoalOther = data.accountGoalOther;
      if (data.sourceOfFunds && Array.isArray(data.sourceOfFunds)) updates.sourceOfFunds = data.sourceOfFunds;
      if (data.sourceOfFundsText) updates.sourceOfFundsText = data.sourceOfFundsText;
      if (data.soloKQuestionnaire) {
        updates.soloKQuestionnaire = {
          ...getEmptySoloKQuestionnaire(),
          ...data.soloKQuestionnaire,
          ownershipEvidence: Array.isArray(data.soloKQuestionnaire.ownershipEvidence)
            ? data.soloKQuestionnaire.ownershipEvidence
            : [],
        };
      }
      if (data.minorName) updates.minorName = data.minorName;
      if (data.relationshipToCustodian) updates.relationshipToCustodian = data.relationshipToCustodian;
      if (data.businessName) updates.businessName = data.businessName;
      if (data.businessEinLast4) updates.businessEinLast4 = data.businessEinLast4;

      // Section 3: Risk Questionnaire — restore original answers if available
      if (data.riskScores) {
        const rs = data.riskScores;
        if (rs.capacityAnswers) updates.capacityAnswers = rs.capacityAnswers;
        if (rs.willingnessAnswers) updates.willingnessAnswers = rs.willingnessAnswers;
        if (rs.capacityTotal != null) updates.capacityTotal = rs.capacityTotal;
        if (rs.willingnessTotal != null) updates.willingnessTotal = rs.willingnessTotal;
        if (rs.combinedScore != null) updates.combinedScore = rs.combinedScore;
        if (rs.riskCategory) updates.riskCategory = rs.riskCategory;
        if (rs.selectedPortfolio) updates.selectedPortfolio = rs.selectedPortfolio;
        if (rs.portfolioOverride != null) updates.portfolioOverride = rs.portfolioOverride;
        if (rs.overrideReason) updates.overrideReason = rs.overrideReason;
        if (rs.overrideOtherText) updates.overrideOtherText = rs.overrideOtherText;
      }

      // Section 4: Fees
      const savedManagementFeeTier = data.managementFeeTierKey || data.iarFeeTier || "";
      if (savedManagementFeeTier) {
        updates.iarFeeTier = this.toTierSelectionValue(savedManagementFeeTier);
        updates.selectedManagementFeeTierKey = normalizeTierKey(savedManagementFeeTier);
      }
      if (data.iarFeeType) updates.iarFeeType = data.iarFeeType;
      if (data.iarFeeFlat) updates.iarFeeFlat = data.iarFeeFlat;
      if (data.iarFeeTiered) updates.iarFeeTiered = data.iarFeeTiered;
      if (data.iarFeeScheduleType) updates.iarFeeScheduleType = data.iarFeeScheduleType;
      if (data.fusionAllocations) updates.fusionAllocations = data.fusionAllocations;

      if ((data.strategy || "").toLowerCase().includes("fusion")) {
        const normalizedFusionState = this.normalizeLegacyFusionFeeState(
          savedManagementFeeTier,
          data.fusionAllocations
        );
        if (normalizedFusionState.iarFeeTier) {
          updates.iarFeeTier = normalizedFusionState.iarFeeTier;
          updates.selectedManagementFeeTierKey = normalizedFusionState.selectedManagementFeeTierKey;
        } else if (String(data.iarFeeTier || "").toLowerCase() === "blended") {
          updates.iarFeeTier = "";
          updates.selectedManagementFeeTierKey = "";
        }
        updates.fusionAllocations = normalizedFusionState.fusionAllocations;
      }

      if (Object.keys(updates).length > 0) {
        this.setState(updates);
      }
    } catch (e) {
      console.error("Error loading ISP edit data:", e);
      sessionStorage.removeItem("editISPData");
      sessionStorage.removeItem("editISPDateTime");
    }
  };

  normalizeProposalPrefillRegistrations = (registrations) => {
    let rawRegistrations = registrations;

    if (typeof registrations === "string") {
      try {
        const parsed = JSON.parse(registrations);
        rawRegistrations = Array.isArray(parsed) ? parsed : registrations;
      } catch {
        rawRegistrations = registrations;
      }
    }

    const rows = Array.isArray(rawRegistrations)
      ? rawRegistrations
      : [rawRegistrations];

    return rows
      .map((registration, index) => {
        if (typeof registration === "string") {
          return {
            registration: `registration${index}`,
            value: registration,
            percentage: index === 0 ? "100" : "",
          };
        }

        if (!registration || typeof registration !== "object") return null;

        return {
          registration: registration.registration || `registration${index}`,
          value: registration.value || registration.name || registration.label || "",
          percentage:
            registration.percentage !== undefined
              ? registration.percentage
              : index === 0
                ? "100"
                : "",
        };
      })
      .filter((registration) => registration?.value);
  };

  checkForProposalPrefill = () => {
    try {
      const raw = sessionStorage.getItem("proposalToISPData");
      if (!raw) return;
      sessionStorage.removeItem("proposalToISPData");

      const data = JSON.parse(raw);
      const updates = {};

      if (data.advisorName) updates.advisorName = data.advisorName;
      if (data.strategy) updates.strategy = data.strategy;
      if (data.accountValue) updates.initialInvestmentAmount = data.accountValue;
      if (data.bondType) updates.bondType = data.bondType;
      if (data.riskTolerance) updates.selectedPortfolio = data.riskTolerance;

      // Handle registration — may be a JSON array string or simple value
      if (
        data.registration !== undefined &&
        data.registration !== null &&
        data.registration !== ""
      ) {
        const registrations = this.normalizeProposalPrefillRegistrations(
          data.registration,
        );
        if (registrations.length > 0) {
          updates.registrations = registrations;
        }
      }

      // Handle client name — split into first/last and add as joint client
      if (data.clientName) {
        const names = data.clientName.split("&").map((n) => n.trim());
        const jointClients = names.map((name, idx) => {
          const parts = name.split(" ");
          return {
            firstName: parts[0] || "",
            middleName: parts.length > 2 ? parts.slice(1, -1).join(" ") : "",
            lastName: parts.length > 1 ? parts[parts.length - 1] : "",
            clientName: name,
            isPrimary: idx === 0,
          };
        });
        updates.jointClients = jointClients;
      }

      // Show info banner for fusion proposals
      if (data.isFusion) {
        updates.proposalPrefillInfo =
          "Pre-filled from a Fusion proposal. Client details may need to be adjusted since Fusion proposals contain multiple strategies.";
      }

      // Preserve proposal's DateTime for merging onto the same row
      if (data.dateTime) {
        updates.proposalDateTime = new Date(data.dateTime).toUTCString();
      }

      if (Object.keys(updates).length > 0) {
        this.setState(updates);
      }
    } catch (e) {
      console.log("Error loading proposal pre-fill data:", e);
    }
  };

  loadRiskChart = async () => {
    try {
      const result = await getS3File("vicus-proposals", "Templates/ISP/risk-q5-chart.png");
      if (result?.data && this._mounted) {
        this.setState({ riskChartUrl: `data:image/png;base64,${result.data}` });
      }
    } catch (e) {
      console.log("Could not load risk chart:", e.message);
    }
  };

  componentWillUnmount() {
    this._mounted = false;
    document.removeEventListener("keydown", this.handleGlobalKeyDown);
  }

  handleGlobalKeyDown = (e) => {
    if (e.key === "Escape") {
      if (this.state.showZeroFeeModal) {
        this.handleCancelZeroFee();
      }
      // Don't close success modal on Escape — user should choose an action
    }
  };

  componentDidUpdate(prevProps, prevState) {
    // Auto-set default advisor when lists load
    const defaultAdvisor = this.props.lists?.userInfo?.DefaultAdvisor;
    const prevDefaultAdvisor = prevProps.lists?.userInfo?.DefaultAdvisor;
    if (defaultAdvisor && defaultAdvisor !== prevDefaultAdvisor && !this.state.advisorName) {
      this.setState({ advisorName: defaultAdvisor });
    }

    const evolutionTiers = this.props.lists?.userInfo?.EvolutionTiers;
    const prevTiers = prevProps.lists?.userInfo?.EvolutionTiers;
    if (this.state.strategy !== prevState.strategy) {
      this.fetchManagementFeeTierOptions();
    } else if (evolutionTiers !== prevTiers) {
      // An EvolutionTiers change re-derives the available tier options. For
      // Fusion the tier list IS the Evolution tiers, so re-seed from them.
      const rawTiers = this.isFusionStrategy()
        ? ["Standard", ...splitEvolutionTiers(evolutionTiers)]
        : this.state.availableManagementFeeTiers;
      this.syncManagementFeeTierOptions(rawTiers);
    }

    // Fetch custom fees when advisor or strategy changes (covers mount, prefill, and direct setState)
    if (this.state.advisorName !== prevState.advisorName || this.state.strategy !== prevState.strategy) {
      this.fetchCustomFees();
    }

    // If the advisor changed and the currently selected strategy is now
    // advisor-scoped away, clear it so the description/validation don't cling.
    const advisorChanged =
      this.state.advisorName !== prevState.advisorName ||
      this.state.additionalAdvisors !== prevState.additionalAdvisors;
    if (advisorChanged && this.state.strategy) {
      const proposalTypes = this.props.lists?.proposalTypes || [];
      const pt = proposalTypes.find((p) => p.name === this.state.strategy);
      const candidates = [
        this.state.advisorName,
        ...(this.state.additionalAdvisors || []),
      ];
      if (pt && !isStrategyAvailableForAdvisors(pt, candidates)) {
        this.setState({ strategy: "" });
      }
    }

    // Enforce the single-available-bond-type lock for paths that set
    // modelSelection directly (edit-restore, proposal prefill) and so bypass
    // handleFieldChange. The !== guard makes this idempotent and loop-safe.
    if (
      this.state.modelSelection !== prevState.modelSelection ||
      this.state.strategy !== prevState.strategy
    ) {
      const available = this.getAvailableBondTypesForModel();
      if (available.length === 1 && this.state.bondType !== available[0]) {
        this.setState({ bondType: available[0] });
      }
    }

    // Fetch management fees when strategy or the resolved management fee tier changes
    if (
      this.state.strategy !== prevState.strategy ||
      this.state.selectedManagementFeeTierKey !== prevState.selectedManagementFeeTierKey
    ) {
      this.fetchManagementFees();
    }
  }

  // Build client name history from previous submissions for autocomplete
  getClientNameHistory = () => {
    const names = [];
    const proposals = this.props.lists?.proposals || [];
    proposals.forEach((p) => {
      if (p["CLIENT NAMES"] && !names.includes(p["CLIENT NAMES"])) {
        names.push(p["CLIENT NAMES"]);
      }
    });
    return names;
  };

  // --- Section toggle ---
  toggleSection = (section) => {
    const { expandedSection } = this.state;

    // If leaving section 4 (collapsing or navigating away), check for zero fees
    if (expandedSection === "4" && !this.state.iarFeeWaiver && this.getEffectiveIarFeeType()) {
      const zeroFields = this.findZeroFees();
      if (zeroFields.length > 0) {
        // Collapsing section 4 → pending is ""; opening another → pending is that section
        const pendingSection = section === "4" ? "" : section;
        this.setState({ showZeroFeeModal: true, pendingSection });
        return;
      }
    }

    this.setState((prev) => {
      const updates = {
        expandedSection: prev.expandedSection === section ? "" : section,
      };
      if (section === "4") {
        updates.feesSectionVisited = true;
      }
      return updates;
    });
  };

  getFeeScheduleLabels = () => {
    const customFees = this.state.customFees;
    let schedules = null;

    // Mirror ispFeeSchedule.getFeeSchedules() logic so validation uses the same labels
    if (customFees?.feeSchedules && typeof customFees.feeSchedules === "object") {
      const parsed = {};
      for (const [name, tiers] of Object.entries(customFees.feeSchedules)) {
        if (Array.isArray(tiers)) {
          parsed[name] = sortFeeTiers(tiers.map((t) => t.label || t));
        }
      }
      if (Object.keys(parsed).length > 0) schedules = parsed;
    }
    if (!schedules && customFees?.tiers && Array.isArray(customFees.tiers)) {
      schedules = { "Schedule 1": sortFeeTiers(customFees.tiers.map((t) => t.label || t)) };
    }
    if (!schedules) {
      schedules = { "Schedule 1": ["First $100,000", "Next $150,000", "Next $250,000", "Next $500,000", "Next $1,000,000", "Next $2,000,000", "Over $4,000,000"] };
    }

    return schedules[this.state.iarFeeScheduleType] || Object.values(schedules)[0] || [];
  };

  findZeroFees = () => {
    const labels = this.getFeeScheduleLabels();
    const zeros = [];
    const collect = (type, flat, tiered) => {
      if (type === "Fixed Percentage") {
        if (flat === "" || parseFloat(flat) === 0) {
          zeros.push("Fixed Percentage Rate");
        }
        return;
      }
      if (type === "Tiered") {
        const tieredRates = normalizeIarFeeTiered(tiered, labels);
        const labelOccurrences = {};
        tieredRates.forEach((tier) => {
          const occurrence = (labelOccurrences[tier.label] = (labelOccurrences[tier.label] || 0) + 1);
          const val = tier.rate;
          if (val === undefined || val === "" || parseFloat(val) === 0) {
            zeros.push(`${tier.label}${occurrence > 1 ? ` (#${occurrence})` : ""}`);
          }
        });
      }
    };
    if (this.hasPath()) {
      collect(
        this.state.iarFeeType || "Fixed Percentage",
        this.state.iarFeeFlat,
        this.state.iarFeeTiered,
      );
      collect(this.state.pathIarFeeType, this.state.pathIarFeeFlat, this.state.pathIarFeeTiered);
    } else {
      collect(this.getEffectiveIarFeeType(), this.state.iarFeeFlat, this.state.iarFeeTiered);
    }
    return zeros;
  };

  handleConfirmZeroFee = () => {
    const zeroFields = this.findZeroFees();
    const userEmail = this.props.user || "unknown";
    const waiver = {
      confirmedAt: new Date().toLocaleString(),
      confirmedBy: userEmail,
      zeroFields,
    };
    const pendingSection = this.state.pendingSection;
    this.setState((prev) => {
      const updates = {
        iarFeeWaiver: waiver,
        showZeroFeeModal: false,
        feesSectionVisited: true,
        pendingSection: null,
      };
      // Auto-set empty values to "0" on confirmation.
      if (this.hasPath()) {
        const initialType = prev.iarFeeType || "Fixed Percentage";
        if (
          initialType === "Fixed Percentage" &&
          (prev.iarFeeFlat === "" || prev.iarFeeFlat === undefined)
        ) {
          updates.iarFeeFlat = "0";
        }
        if (initialType === "Tiered") {
          const labels = this.getFeeScheduleLabels();
          updates.iarFeeTiered = normalizeIarFeeTiered(prev.iarFeeTiered, labels).map((tier) =>
            tier.rate === undefined || tier.rate === "" ? { ...tier, rate: "0" } : tier,
          );
        }
      } else {
        const effectiveType = this.isFlatFeeOnlyStrategy() ? "Fixed Percentage" : prev.iarFeeType;
        if (effectiveType === "Fixed Percentage" && (prev.iarFeeFlat === "" || prev.iarFeeFlat === undefined)) {
          updates.iarFeeFlat = "0";
        }
        if (effectiveType === "Tiered") {
          const labels = this.getFeeScheduleLabels();
          const updated = normalizeIarFeeTiered(prev.iarFeeTiered, labels).map((tier) =>
            tier.rate === undefined || tier.rate === "" ? { ...tier, rate: "0" } : tier,
          );
          updates.iarFeeTiered = updated;
        }
      }
      if (this.hasPath()) {
        if (prev.pathIarFeeType === "Fixed Percentage" &&
            (prev.pathIarFeeFlat === "" || prev.pathIarFeeFlat === undefined)) {
          updates.pathIarFeeFlat = "0";
        }
        if (prev.pathIarFeeType === "Tiered") {
          const labels = this.getFeeScheduleLabels();
          updates.pathIarFeeTiered = normalizeIarFeeTiered(prev.pathIarFeeTiered, labels).map((tier) =>
            tier.rate === undefined || tier.rate === "" ? { ...tier, rate: "0" } : tier,
          );
        }
      }
      return updates;
    }, () => {
      // Delegate navigation to toggleSection so visited flags are set through the normal flow.
      // The waiver is now committed to state, so toggleSection's zero-fee check will be skipped.
      this.toggleSection(pendingSection === "" ? "4" : pendingSection);
    });
  };

  handleCancelZeroFee = () => {
    this.setState({ showZeroFeeModal: false, pendingSection: null });
  };

  // --- Generic field handler ---
  isReducedExistingAccountAction = (action) =>
    action === "Risk Tolerance Update" ||
    action === "Model Change" ||
    action === "Fee Update";

  getSingleRegistrationState = () => {
    const first = this.state.registrations?.[0] || {
      registration: "registration0",
      value: "",
      percentage: "100",
    };
    return [
      {
        ...first,
        registration: "registration0",
        percentage: first.percentage || "100",
      },
    ];
  };

  getReducedAccountInfoReset = () => ({
    custodian: "",
    bondType: "Taxable",
    modelSelection: "",
    pathBondType: "",
    initialInvestmentAmount: "",
    accountNetWorthPct: "",
    accountIncomeNeed: "",
    accountIncomeAmount: "",
    accountIncomeStartDate: "",
    accountIncomeEndDate: "",
    accountIncomeFrequency: "",
    accountGoal: "",
    accountGoalOther: "",
    sourceOfFunds: [],
    sourceOfFundsText: {},
    minorName: "",
    relationshipToCustodian: "",
    businessName: "",
    businessEinLast4: "",
  });

  getRiskToleranceReset = () => ({
    capacityAnswers: {},
    willingnessAnswers: {},
    capacityTotal: 0,
    willingnessTotal: 0,
    combinedScore: 0,
    riskCategory: "",
    selectedPortfolio: "",
    portfolioOverride: false,
    overrideReason: "",
    overrideOtherText: "",
  });

  handleFieldChange = (field, value) => {
    const updates = field === "iarFeeTier"
      ? (() => {
        const resolvedSelection = resolveManagementFeeTierSelection(
          value,
          this.state.availableManagementFeeTierOptions
        );
        return {
          iarFeeTier: resolvedSelection.label,
          selectedManagementFeeTierKey: resolvedSelection.exactTier,
        };
      })()
      : { [field]: value };
    // Clear portfolio selection when strategy or bond type changes
    if (field === "strategy" || field === "bondType") {
      updates.selectedPortfolio = "";
    }
    if (field === "requestType" && value !== "Existing") {
      updates.existingRequestAction = "";
      updates.accountNumbers = [""];
    }
    if (field === "requestType" && value !== "New") {
      updates.clientProfileImaSubmitted = "";
      updates.householding = "";
      updates.householdRefName = "";
      updates.householdRefLast4 = "";
      updates.householdRef = "";
      updates.householdReason = "";
      updates.householdReasonType = "";
      updates.householdReasonOther = "";
    }
    if (field === "existingRequestAction") {
      updates.accountNumber = "";
      updates.accountNumbers = [""];
      if (value !== "Strategy Change") {
        updates.clientProfileImaSubmitted = "";
      }
      if (value !== "Fee Update") {
        updates.registrations = this.getSingleRegistrationState();
      }
      if (this.isReducedExistingAccountAction(value)) {
        Object.assign(updates, this.getReducedAccountInfoReset());
      }
      if (value === "Fee Update" || value === "Model Change") {
        Object.assign(updates, this.getRiskToleranceReset());
      }
    }
    // Clear model selection and recalculate risk scores when strategy changes
    if (field === "strategy") {
      const nextIsManagedMoneyMarket = this.isManagedMoneyMarketStrategy(value);
      const managedMoneyMarketModeChanged =
        this.isManagedMoneyMarketStrategy(this.state.strategy) !== nextIsManagedMoneyMarket;
      updates.modelSelection = "";
      updates.iarFeeTier = "";
      updates.selectedManagementFeeTierKey = "";
      updates.availableManagementFeeTiers = [];
      updates.availableManagementFeeTierOptions = [];
      updates.fusionAllocations = [];
      updates.fusionSubFees = {};
      updates.fusionSubFeesLoading = false;
      updates.fusionFeeValidation = { isValid: true, issues: [] };
      updates.fusionRegistrationType = String(value || "")
        .toLowerCase()
        .includes("fusion")
        ? "Single"
        : "";
      updates.fusionRebalanceType = String(value || "")
        .toLowerCase()
        .includes("fusion")
        ? "Automatic"
        : "";
      if (managedMoneyMarketModeChanged) {
        updates.accountGoal = nextIsManagedMoneyMarket ? [] : "";
        updates.accountGoalOther = "";
      }
      Object.assign(updates, computeRiskScores(this.state.capacityAnswers, this.state.willingnessAnswers));
      // Recompute the path bond default for the newly selected strategy
      // (state.strategy isn't updated yet, so inspect `value`).
      const pathPt = this.props.lists?.proposalTypes?.find((p) => p.name === value);
      const pathName = pathPt?.pathStrategy || "";
      const pathTypes = pathName ? this.getBondTypesForStrategyName(pathName) : [];
      const advHasChoice = pathTypes.includes("Taxable") && pathTypes.includes("Municipal");
      updates.pathBondType = advHasChoice ? "Taxable" : "";
    }
    if (field === "fusionRegistrationType") {
      if (value === "Multi") {
        updates.fusionRebalanceType = "Manual";
      } else if (!this.state.fusionRebalanceType) {
        updates.fusionRebalanceType = "Automatic";
      }
    }
    // Lock the bond type when the chosen program only exists under one bond
    // type (e.g. Government Money Market is taxable only). Clearing the
    // portfolio mirrors the bondType-change branch above since the narrowed
    // risk-tolerance options change with it.
    if (field === "modelSelection") {
      const available = this.getAvailableBondTypesForModel(value);
      if (available.length === 1 && this.state.bondType !== available[0]) {
        updates.bondType = available[0];
        updates.selectedPortfolio = "";
      }
      // Even when the bond type isn't locked, a new program narrows the
      // risk-tolerance list. Clear a now-orphaned selection so the Section 3
      // dropdown doesn't show its placeholder while keeping a "valid" (green)
      // border driven by the stale selectedPortfolio value.
      if (updates.selectedPortfolio !== "" && this.state.selectedPortfolio) {
        const nextBondType = updates.bondType || this.state.bondType;
        const options = this.getRiskToleranceOptions({
          modelSelection: value,
          bondType: nextBondType,
        });
        if (options.length > 0 && !options.includes(this.state.selectedPortfolio)) {
          updates.selectedPortfolio = "";
        }
      }
    }
    // Clear fee waiver if fee values change
    if (["iarFeeType", "iarFeeFlat", "iarFeeTiered", "iarFeeScheduleType"].includes(field)) {
      updates.iarFeeWaiver = null;
    }
    this.setState(updates);
  };

  fetchCustomFees = async () => {
    const { advisorName, strategy } = this.state;
    if (!advisorName) return;
    try {
      const result = await getCustomFees(advisorName, strategy || "");
      const fees = result?.body ? JSON.parse(result.body) : result;
      if (fees && typeof fees === "object" && !fees.errorMessage) {
        this.setState({ customFees: fees });
      }
    } catch (e) {
      console.log("Failed to load custom fees:", e.message);
    }
  };

  getSelectedManagementFeeTierKey = (
    selection = this.state.selectedManagementFeeTierKey || this.state.iarFeeTier
  ) => {
    const resolvedSelection = resolveManagementFeeTierSelection(
      selection,
      this.state.availableManagementFeeTierOptions
    );
    return resolvedSelection.exactTier || this.state.selectedManagementFeeTierKey || "";
  };

  getManagementFeeTierCandidates = (
    selection = this.state.selectedManagementFeeTierKey || this.state.iarFeeTier
  ) => {
    const collapsedLabel = collapseTierLabel(selection);
    if (!collapsedLabel) return [];

    const strategyExactTiers = (this.state.availableManagementFeeTiers || [])
      .map((tier) => normalizeTierKey(tier))
      .filter(Boolean);
    const userExactTiers = splitEvolutionTiers(this.props.lists?.userInfo?.EvolutionTiers)
      .map((tier) => normalizeTierKey(tier))
      .filter(Boolean);
    const strategyTierSet = new Set(strategyExactTiers);
    const candidates = [];

    const pushCandidate = (tier) => {
      if (!tier || candidates.includes(tier)) return;
      candidates.push(tier);
    };

    const selectedTier = this.getSelectedManagementFeeTierKey(selection);
    pushCandidate(selectedTier);

    userExactTiers.forEach((tier) => {
      if (collapseTierLabel(tier) !== collapsedLabel) return;
      if (strategyExactTiers.length > 0 && !strategyTierSet.has(tier)) return;
      pushCandidate(tier);
    });

    strategyExactTiers.forEach((tier) => {
      if (collapseTierLabel(tier) === collapsedLabel) {
        pushCandidate(tier);
      }
    });

    return candidates;
  };

  loadManagementFeesForTierCandidates = async (strategy, tierCandidates = []) => {
    // Try every candidate in priority order, then fall back to the
    // collapsed label (e.g., "Tier B") if it isn't already in the list.
    // Sub-strategies often have management-fee rows keyed by the IM tier
    // alone, even when the user's selected tier carries an IAR-schedule
    // suffix (e.g., "Tier B WK"). Without this fallback, Fusion sleeves
    // whose available tiers differ from the parent strategy's surface
    // "no tier schedule found" even though the data exists.
    const seen = new Set();
    const tryList = [];
    const push = (t) => {
      if (!t || seen.has(t)) return;
      seen.add(t);
      tryList.push(t);
    };
    tierCandidates.forEach(push);
    tierCandidates.forEach((t) => push(collapseTierLabel(t)));

    for (const tier of tryList) {
      try {
        const result = await getManagementFees(strategy, tier);
        const fees = result?.body ? JSON.parse(result.body) : result;
        if (fees && typeof fees === "object" && !fees.errorMessage && Object.keys(fees).length > 0) {
          return {
            tier,
            fees: sortManagementFeeTiers(fees),
          };
        }
      } catch (e) {
        console.log(`Failed to load management fees for ${strategy} at ${tier}:`, e.message);
      }
    }

    return {
      tier: tierCandidates[0] || "",
      fees: null,
    };
  };

  syncManagementFeeTierOptions = (
    rawTiers = this.state.availableManagementFeeTiers,
    preferredSelection = this.state.selectedManagementFeeTierKey || this.state.iarFeeTier
  ) => {
    const availableManagementFeeTierOptions = buildManagementFeeTierOptions(
      rawTiers,
      this.props.lists?.userInfo?.EvolutionTiers
    );

    let nextTier = "";
    let nextTierKey = "";

    const resolvedSelection = resolveManagementFeeTierSelection(
      preferredSelection,
      availableManagementFeeTierOptions
    );

    const standardOption = availableManagementFeeTierOptions.find(
      (option) => option.label === "Standard"
    );

    if (resolvedSelection.exactTier) {
      nextTier = resolvedSelection.label;
      nextTierKey = resolvedSelection.exactTier;
    } else if (standardOption) {
      nextTier = standardOption.label;
      nextTierKey = standardOption.exactTier;
    } else if (availableManagementFeeTierOptions.length === 1) {
      nextTier = availableManagementFeeTierOptions[0].label;
      nextTierKey = availableManagementFeeTierOptions[0].exactTier;
    }

    this.setState((prev) => {
      const rawTiersChanged =
        JSON.stringify(prev.availableManagementFeeTiers) !== JSON.stringify(rawTiers);
      const optionsChanged =
        JSON.stringify(prev.availableManagementFeeTierOptions) !== JSON.stringify(availableManagementFeeTierOptions);

      if (
        !rawTiersChanged &&
        !optionsChanged &&
        prev.iarFeeTier === nextTier &&
        prev.selectedManagementFeeTierKey === nextTierKey
      ) {
        return null;
      }

      return {
        availableManagementFeeTiers: rawTiers,
        availableManagementFeeTierOptions,
        iarFeeTier: nextTier,
        selectedManagementFeeTierKey: nextTierKey,
      };
    });
  };

  fetchManagementFeeTierOptions = async () => {
    const { strategy } = this.state;
    const fallbackTiers = splitEvolutionTiers(this.props.lists?.userInfo?.EvolutionTiers);
    if (!strategy) {
      this.setState({
        availableManagementFeeTiers: [],
        availableManagementFeeTierOptions: [],
        iarFeeTier: "",
        selectedManagementFeeTierKey: "",
      });
      return;
    }

    // Fusion has no parent-level tier table — each sub-strategy carries its
    // own schedule. The tier choice is dictated purely by the user's
    // Evolution tiers, run through the same buildManagementFeeTierOptions
    // collapsing logic as every other strategy. The "Standard" baseline keeps
    // the "no Evolution tiers" / "standard-only" cases collapsing to a single
    // Standard option (subsection hidden), matching non-Fusion behavior.
    if (this.isFusionStrategy()) {
      this.syncManagementFeeTierOptions(["Standard", ...fallbackTiers]);
      return;
    }

    try {
      const result = await getManagementFeeTiers(strategy);
      const tierResponse = result?.body ? JSON.parse(result.body) : result;
      const rawTiers = Array.isArray(tierResponse?.tiers) && tierResponse.tiers.length > 0
        ? tierResponse.tiers
        : fallbackTiers;
      if (!this._mounted) return;
      this.syncManagementFeeTierOptions(rawTiers);
    } catch (e) {
      console.log("Failed to load management fee tiers:", e.message);
      if (!this._mounted) return;
      this.syncManagementFeeTierOptions(fallbackTiers);
    }
  };

  isFusionStrategy = () => this.state.strategy?.toLowerCase().includes("fusion") || false;

  toTierSelectionValue = (tier) => {
    return collapseTierLabel(tier);
  };

  normalizeFusionAllocations = (fusionAllocations = []) => {
    if (!Array.isArray(fusionAllocations)) return [];
    return fusionAllocations.map((allocation) => ({
      strategy: allocation?.strategy || "",
      riskTolerance: allocation?.riskTolerance || "",
      taxSensitivity: allocation?.taxSensitivity || "",
      registration: allocation?.registration || "",
      accountOwner: allocation?.accountOwner || "",
      displayName:
        allocation?.displayName ||
        getFusionAllocationDisplayName(allocation, this.props.lists?.proposalTypes || []),
      percentage: allocation?.percentage ?? "",
    }));
  };

  normalizeLegacyFusionFeeState = (iarFeeTier, fusionAllocations = []) => {
    const normalizedAllocations = this.normalizeFusionAllocations(fusionAllocations);
    const legacyTier =
      fusionAllocations.find((allocation) => allocation?.tier)?.tier || "";
    const normalizedTier =
      iarFeeTier && String(iarFeeTier).toLowerCase() !== "blended"
        ? iarFeeTier
        : legacyTier || "";

    return {
      iarFeeTier: this.toTierSelectionValue(normalizedTier),
      selectedManagementFeeTierKey: normalizeTierKey(normalizedTier),
      fusionAllocations: normalizedAllocations,
    };
  };

  getFusionFeeKey = (strategy, tier = this.getSelectedManagementFeeTierKey()) => {
    if (!strategy || !tier) return "";
    return `${strategy}|${tier}`;
  };

  normalizeTier = (tier) => {
    return normalizeTierKey(tier);
  };

  fetchManagementFees = async () => {
    const { strategy } = this.state;
    const tierCandidates = this.getManagementFeeTierCandidates();
    // Bump the token up front so any standard fetch already in flight is
    // invalidated, even when this call routes to the Fusion path instead.
    const requestId = ++this._managementFeesRequestId;
    if (!strategy || tierCandidates.length === 0) {
      this.setState({
        managementFees: null,
        managementFeesLoading: false,
        fusionSubFees: {},
        fusionSubFeesLoading: false,
        fusionFeeValidation: { isValid: true, issues: [] },
      });
      return;
    }
    if (this.isFusionStrategy()) {
      this.setState({
        managementFees: null,
        managementFeesLoading: false,
        fusionFeeValidation: { isValid: true, issues: [] },
      });
      this.fetchFusionSubFees();
      return;
    }
    this.setState({ managementFeesLoading: true, managementFees: null });
    try {
      const { tier, fees } = await this.loadManagementFeesForTierCandidates(strategy, tierCandidates);
      if (!this._mounted || requestId !== this._managementFeesRequestId) return;
      if (fees) {
        // Flat-fee strategies that name an pathStrategy preview the
        // post-path tier schedule alongside the flat row. Inject the
        // path row's tieredRates into `fees` so the existing tier
        // table renders without touching the schedule component.
        const pathName = this.getPathStrategyName();
        if (pathName && fees.flatFee != null) {
          try {
            const advResult = await getManagementFees(pathName, tier);
            if (!this._mounted || requestId !== this._managementFeesRequestId) return;
            // getManagementFees returns the raw Lambda response shape
            // ({statusCode, body}) for live calls, or the plain object in tests.
            // Mirror the parse used by loadManagementFeesForTierCandidates.
            const pathFees = advResult?.body ? JSON.parse(advResult.body) : advResult;
            if (pathFees?.tieredRates?.length > 0) {
              fees.tieredRates = sortFeeTiers(pathFees.tieredRates);
              // Carry the path row's minimum so the schedule can render
              // its own phase-labeled note under the tier table.
              if (pathFees.minimumFee != null) {
                fees.pathMinimumFee = pathFees.minimumFee;
              }
              // Stable signal that the path mgmt-fees row was fetched —
              // used by hasPath() to gate path-stage IAR rendering/validation.
              fees.pathStrategyRow = true;
            }
          } catch (pathErr) {
            console.log("Failed to load path fees:", pathErr.message);
          }
        }
        this.setState({
          managementFees: fees,
          managementFeesLoading: false,
          selectedManagementFeeTierKey: tier,
        });
      } else {
        this.setState({ managementFeesLoading: false });
      }
    } catch (e) {
      if (!this._mounted || requestId !== this._managementFeesRequestId) return;
      console.log("Failed to load management fees:", e.message);
      this.setState({ managementFeesLoading: false });
    }
  };

  fetchFusionSubFees = async () => {
    const { fusionAllocations } = this.state;
    const tierCandidates = this.getManagementFeeTierCandidates();
    // Bump up front so any earlier fetch in flight is invalidated, including
    // when this call exits early without dispatching new requests.
    const requestId = ++this._fusionSubFeesRequestId;
    // Fetch fees for the FULL eligible-Fusion-sub-strategy roster (not just
    // the user's selected allocations). Compliance requirement: the PDF
    // shows every eligible sub-strategy's tier schedule. The form display
    // only renders the selected ones — filtering happens in
    // buildFusionFeeBlocks (form variant). Sub-strategies the user hasn't
    // allocated to still need their fees cached so the payload carries
    // them.
    const eligible = getEligibleFusionStrategies(
      this.props.lists?.proposalTypes || [],
      { bondType: this.state.bondType, allowIspOnly: true }
    );
    if (tierCandidates.length === 0 || eligible.length === 0) {
      this.setState({
        fusionSubFees: {},
        fusionBlendedFees: null,
        fusionSubFeesLoading: false,
        fusionFeeValidation: { isValid: true, issues: [] },
      });
      return;
    }
    this.setState({
      fusionSubFeesLoading: true,
      fusionFeeValidation: { isValid: true, issues: [] },
    });
    const results = {};
    let resolvedTier = "";
    await Promise.all(eligible.map(async (pt) => {
      const { tier, fees } = await this.loadManagementFeesForTierCandidates(pt.name, tierCandidates);
      if (!fees) return;
      if (!resolvedTier) {
        resolvedTier = tier;
      }
      const key = this.getFusionFeeKey(pt.name, tier);
      if (!results[key]) {
        results[key] = fees;
      }
    }));
    // Discard if a newer tier/allocation change superseded this fetch.
    if (!this._mounted || requestId !== this._fusionSubFeesRequestId) return;
    const effectiveTier = resolvedTier || tierCandidates[0];
    const collapsedEffective = collapseTierLabel(effectiveTier);
    // Validation lookup tries the resolved tier, the collapsed label, and
    // finally any cached key for the strategy. Mirrors the lenient match
    // in buildFusionFeeBlocks so we don't claim "incomplete schedule" for
    // a sub-strategy whose fees did load (just under a tier label that
    // differs from effectiveTier in the IAR suffix).
    const getFeeKey = (subStrategy) => {
      const candidates = [effectiveTier, collapsedEffective].filter(Boolean);
      for (const t of candidates) {
        const key = this.getFusionFeeKey(subStrategy, t);
        if (key && results[key]) return key;
      }
      const prefix = `${subStrategy}|`;
      const fallback = Object.keys(results).find((k) => k.startsWith(prefix));
      return fallback || this.getFusionFeeKey(subStrategy, effectiveTier);
    };
    const fusionFeeValidation = getFusionFeeValidation(fusionAllocations, results, getFeeKey);
    // T4 (round 4) — blended-fee computation removed for compliance: each
    // sub-strategy's full tier schedule is shown individually on the form
    // (selected only) and PDF (full eligible roster).
    this.setState({
      fusionSubFees: results,
      fusionSubFeesLoading: false,
      fusionFeeValidation,
      selectedManagementFeeTierKey: effectiveTier || this.state.selectedManagementFeeTierKey,
    });
  };

  // T4 — locate cached fees for a sub-strategy under the active tier.
  // Tier resolution is forgiving: management-fees rows in DynamoDB are
  // generally keyed by the IM tier (e.g. "Tier B"), but the ISP form's
  // selected tier may include the IAR-schedule suffix (e.g. "Tier B WK").
  // We try the exact selected tier first, then the collapsed label, then
  // any cached entry for this strategy regardless of suffix. The displayed
  // tier is always the collapsed label so the PDF reads "Tier B" rather
  // than the IAR-flavored "Tier B WK".
  resolveFusionSubFees = (strategy, exactTier, collapsed) => {
    const subFees = this.state.fusionSubFees || {};
    const candidates = [exactTier, collapsed].filter(Boolean);
    for (const t of candidates) {
      const key = this.getFusionFeeKey(strategy, t);
      if (key && subFees[key]) return subFees[key];
    }
    const prefix = `${strategy}|`;
    const fallbackKey = Object.keys(subFees).find((k) => k.startsWith(prefix));
    return fallbackKey ? subFees[fallbackKey] : null;
  };

  // T4 (form display) — per-sleeve blocks for the SELECTED Fusion
  // allocations only. Drives the form's Section 4 fee schedule list.
  // Each block carries strategy + allocation weight + tier + tieredRates.
  buildFusionFeeBlocks = () => {
    if (!this.isFusionStrategy()) return [];
    const exactTier = this.getSelectedManagementFeeTierKey();
    const collapsed = collapseTierLabel(exactTier);
    return (this.state.fusionAllocations || [])
      .filter((a) => a && a.strategy)
      .map((a) => {
        const fees = this.resolveFusionSubFees(a.strategy, exactTier, collapsed);
        return {
          strategy: a.strategy,
          displayName:
            a.displayName ||
            getFusionAllocationDisplayName(a, this.props.lists?.proposalTypes || []),
          riskTolerance: a.riskTolerance || "",
          taxSensitivity: a.taxSensitivity || "",
          registration: a.registration || "",
          accountOwner: a.accountOwner || "",
          weight: a.percentage,
          tier: collapsed || exactTier || "",
          tieredRates: fees?.tieredRates || [],
          minimumFee: fees?.minimumFee || "",
        };
      });
  };

  getFusionAllocationRiskValidation = () => {
    const base = {
      isValid: true,
      score: null,
      category: "",
      selectedPortfolio: this.state.selectedPortfolio || "",
      message: "",
    };
    if (!this.isFusionStrategy() || this.shouldSkipRiskTolerance()) return base;

    const result = calculateFusionWeightedRiskScore(
      this.state.fusionAllocations,
      this.props.lists?.proposalTypes || [],
    );
    const summary = {
      ...base,
      score: result.score,
      category: result.category,
    };
    if (result.score === null || result.hasMissingScore) {
      return {
        ...summary,
        isValid: false,
        message:
          "Fusion allocation risk score could not be calculated for every selected sub-strategy.",
      };
    }
    if (!this.state.selectedPortfolio) {
      return {
        ...summary,
        isValid: false,
        message: "Select a Risk Tolerance before submitting this Fusion ISP.",
      };
    }
    const matches = portfolioMatchesRiskCategory(
      this.state.selectedPortfolio,
      result.category,
      this.getRiskToleranceAliases(),
    );
    if (matches) return summary;

    return {
      ...summary,
      isValid: false,
      message: `Fusion allocation risk category (${result.category}) must match the selected Risk Tolerance (${this.state.selectedPortfolio}).`,
    };
  };

  // T4 (payload / PDF) — the FULL roster of Fusion-eligible sub-strategies,
  // one block per eligible strategy. Selected allocations carry their
  // weight; un-selected eligible sub-strategies ship with weight = "" so
  // the PDF can render their tier schedules for compliance without
  // pretending the client chose an allocation.
  buildFusionFeeBlocksForPayload = () => {
    if (!this.isFusionStrategy()) return [];
    const exactTier = this.getSelectedManagementFeeTierKey();
    const collapsed = collapseTierLabel(exactTier);
    const eligible = getEligibleFusionStrategies(
      this.props.lists?.proposalTypes || [],
      { bondType: this.state.bondType, allowIspOnly: true }
    );
    const allocationsByStrategy = new Map();
    (this.state.fusionAllocations || [])
      .filter((a) => a && a.strategy)
      .forEach((allocation) => {
        const entries = allocationsByStrategy.get(allocation.strategy) || [];
        entries.push(allocation);
        allocationsByStrategy.set(allocation.strategy, entries);
      });
    return eligible.map((pt) => {
      const allocations = allocationsByStrategy.get(pt.name) || [];
      const allocation = allocations.length === 1 ? allocations[0] : null;
      const weight = allocations.length
        ? String(
            Number(
              allocations
                .reduce((sum, entry) => sum + (parseFloat(entry.percentage) || 0), 0)
                .toFixed(4)
            )
          )
        : "";
      const fees = this.resolveFusionSubFees(pt.name, exactTier, collapsed);
      return {
        strategy: pt.name,
        displayName:
          allocation?.displayName ||
          getFusionAllocationDisplayName(allocation || { strategy: pt.name }, this.props.lists?.proposalTypes || []),
        riskTolerance: allocation?.riskTolerance || "",
        taxSensitivity: allocation?.taxSensitivity || "",
        registration: allocation?.registration || "",
        accountOwner: allocation?.accountOwner || "",
        weight,
        tier: collapsed || exactTier || "",
        tieredRates: fees?.tieredRates || [],
        minimumFee: fees?.minimumFee || "",
      };
    });
  };

  handleFusionAllocationsChange = (allocations) => {
    this.setState({ fusionAllocations: this.normalizeFusionAllocations(allocations) }, () => {
      this.fetchFusionSubFees();
    });
  };

  handleAccountNumberChange = (index, value) => {
    const newNumbers = [...this.state.accountNumbers];
    newNumbers[index] = value;

    // Auto-add new empty field when the last one is filled
    if (index === newNumbers.length - 1 && value.trim() !== "") {
      newNumbers.push("");
    }

    // Remove empty fields except the last one
    const filtered = newNumbers.filter(
      (num, idx) =>
        num.trim() !== "" ||
        idx === newNumbers.length - 1 ||
        newNumbers.length === 1
    );

    this.setState({ accountNumbers: filtered });
  };

  handleRegistrationChange = (registrationKey) => (e) => {
    const newRegistrations = this.state.registrations.map((reg) => {
      if (reg.registration === registrationKey) {
        return { ...reg, value: e.target.value };
      }
      return reg;
    });

    // If the new registration is Qualified and bond type is Municipal, reset to Taxable
    const updates = { registrations: newRegistrations };
    const regType = this.props.lists?.registrationTypes?.find((r) => r.name === e.target.value);
    if (regType?.["Account Type"] === "Qualified" && this.state.bondType === "Municipal") {
      updates.bondType = "Taxable";
    }
    if (regType?.["Account Type"] === "Qualified" && this.state.pathBondType === "Municipal") {
      updates.pathBondType = "Taxable";
    }
    if (regType?.category !== "Business Account") {
      updates.businessName = "";
      updates.businessEinLast4 = "";
    }
    if (!this.hasSoloKRegistration(newRegistrations)) {
      updates.soloKQuestionnaire = getEmptySoloKQuestionnaire();
    }

    this.setState(updates);
  };

  addRegistration = () => {
    this.setState((prev) => ({
      registrations: [
        ...prev.registrations,
        {
          registration: `registration${prev.registrations.length}`,
          value: "",
          percentage: "100",
        },
      ],
    }));
  };

  removeRegistration = (index) => {
    this.setState((prev) => {
      if (prev.registrations.length <= 1) return null;
      const registrations = prev.registrations.filter((_, i) => i !== index);
      return {
        registrations,
        ...(!this.hasSoloKRegistration(registrations)
          ? { soloKQuestionnaire: getEmptySoloKQuestionnaire() }
          : {}),
      };
    });
  };

  handleSoloKQuestionnaireChange = (field, value) => {
    this.setState((prev) => ({
      soloKQuestionnaire: {
        ...getEmptySoloKQuestionnaire(),
        ...prev.soloKQuestionnaire,
        [field]: value,
      },
    }));
  };

  handleSoloKOwnershipEvidenceToggle = (evidenceName) => {
    this.setState((prev) => {
      const current = prev.soloKQuestionnaire?.ownershipEvidence || [];
      const ownershipEvidence = current.includes(evidenceName)
        ? current.filter((evidence) => evidence !== evidenceName)
        : [...current, evidenceName];
      return {
        soloKQuestionnaire: {
          ...getEmptySoloKQuestionnaire(),
          ...prev.soloKQuestionnaire,
          ownershipEvidence,
        },
      };
    });
  };

  handleSourceOfFundsToggle = (fundName) => {
    this.setState((prev) => {
      const selected = prev.sourceOfFunds.includes(fundName)
        ? prev.sourceOfFunds.filter((f) => f !== fundName)
        : [...prev.sourceOfFunds, fundName];
      return { sourceOfFunds: selected };
    });
  };

  handleSourceOfFundsTextChange = (fundName, text) => {
    this.setState((prev) => ({
      sourceOfFundsText: { ...prev.sourceOfFundsText, [fundName]: text },
    }));
  };

  // --- Section 1: Joint Clients CRUD ---
  showAddClientDialog = () => {
    this.setState({
      showClientDialog: true,
      dialogEditIndex: -1,
    });
  };

  showEditClientDialog = (index) => {
    this.setState({
      showClientDialog: true,
      dialogEditIndex: index,
    });
  };

  hideClientDialog = () => {
    this.setState({ showClientDialog: false, dialogEditIndex: -1, dialogCopySource: -1 });
  };

  handleSaveClient = (clientData) => {
    this.setState((prev) => {
      const client = {
        ...clientData,
        id: prev.dialogEditIndex >= 0
          ? prev.jointClients[prev.dialogEditIndex].id
          : `client_${Date.now()}`,
      };

      let jointClients;
      if (prev.dialogEditIndex >= 0) {
        jointClients = prev.jointClients.map((c, i) =>
          i === prev.dialogEditIndex ? client : c
        );
      } else {
        jointClients = [...prev.jointClients, client];
      }

      // If this client is marked primary, unmark all others
      if (client.isPrimary) {
        jointClients = jointClients.map((c) =>
          c.id === client.id ? c : { ...c, isPrimary: false }
        );
      }

      // Keep the primary client at the top of the list so the displayed order
      // (and everything derived from it) always leads with the primary holder.
      jointClients = [
        ...jointClients.filter((c) => c.isPrimary),
        ...jointClients.filter((c) => !c.isPrimary),
      ];

      return { jointClients, showClientDialog: false, dialogEditIndex: -1, dialogCopySource: -1 };
    });
  };

  copyClient = (index) => {
    this.setState({
      showClientDialog: true,
      dialogEditIndex: -1,
      dialogCopySource: index,
    });
  };

  removeClient = (index) => {
    const client = this.state.jointClients[index];
    const name = [client?.firstName, client?.lastName].filter(Boolean).join(" ") || "this client";
    this.setState({
      confirmModal: {
        title: "Remove Client",
        message: `Are you sure you want to remove ${name}?`,
        onConfirm: () => this.setState((prev) => {
          const jointClients = prev.jointClients.filter((_, i) => i !== index);
          return { jointClients, confirmModal: null };
        }),
      },
    });
  };

  // Primary client first, joint clients in original entry order. Used for
  // display, payload serialization, and combined-name building so the PDF
  // never shows a co-holder ahead of the primary.
  getOrderedJointClients = () => {
    const { jointClients } = this.state;
    const primary = jointClients.filter((c) => c.isPrimary);
    const others = jointClients.filter((c) => !c.isPrimary);
    return [...primary, ...others];
  };

  // Build combined client name string for display and proposal pre-fill
  // Same last name: "John & Jane Smith"
  // Different last names: "John Smith & Jane Doe"
  // Three+ clients: "John A. Smith & Jane Doe & Bob Smith"
  getCombinedClientName = () => {
    const jointClients = this.getOrderedJointClients();
    if (jointClients.length === 0) return "";
    if (jointClients.length === 1) return jointClients[0].clientName;

    // Extract last names for comparison
    const clients = jointClients.map((c) => {
      const parts = c.clientName.trim().split(/\s+/);
      const last = parts.length > 1 ? parts[parts.length - 1] : "";
      const rest = parts.length > 1 ? parts.slice(0, -1).join(" ") : parts[0];
      return { first: rest, last, full: c.clientName };
    });

    // Check if all clients share the same last name
    const allSameLast = clients.every(
      (c) => c.last && c.last.toLowerCase() === clients[0].last.toLowerCase()
    );

    if (allSameLast && clients[0].last) {
      // "John & Jane Smith" or "John & Jane & Bob Smith"
      const firsts = clients.map((c) => c.first).join(" & ");
      return `${firsts} ${clients[0].last}`;
    }

    // Different last names: "John Smith & Jane Doe"
    return clients.map((c) => c.full).join(" & ");
  };

  // Check if selected strategy supports both bond types AND registration is Non-Qualified
  hasBondOptions = () => {
    const { strategy, registrations } = this.state;
    if (!strategy) return false;
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    if (!pt || !pt.taxableBonds || !pt.municipalBonds) return false;

    // Qualified accounts (retirement, HSA) are always taxable — hide the bond type choice
    const selectedRegValue = this.isFusionMultiRegistration()
      ? ""
      : registrations[0]?.value;
    if (selectedRegValue) {
      const regType = this.props.lists?.registrationTypes?.find((r) => r.name === selectedRegValue);
      if (regType?.["Account Type"] === "Qualified") return false;
    }

    return true;
  };

  // Get risk tolerance options from strategy's bond-type arrays (same as proposal generator)
  getStrategyDescription = () => {
    const { strategy } = this.state;
    if (!strategy) return "";
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    return (
      pt?.description ||
      `Client is choosing to participate in the ${this.getStrategyDisplayName(
        strategy,
      )} program under this Agreement.`
    );
  };

  getStrategyDisplayName = (strategy = this.state.strategy) =>
    getProgramDisplayName(strategy, this.props.lists?.proposalTypes);

  getFusionStrategyDescriptions = () => {
    if (!this.isFusionStrategy()) return [];
    const proposalTypes = this.props.lists?.proposalTypes || [];
    const seen = new Set();
    return (this.state.fusionAllocations || [])
      .map((allocation) => allocation?.strategy)
      .filter(Boolean)
      .filter((strategy) => {
        if (seen.has(strategy)) return false;
        seen.add(strategy);
        return true;
      })
      .map((strategy) => {
        const pt = proposalTypes.find((p) => p.name === strategy);
        const displayName = getProgramDisplayName(strategy, proposalTypes);
        return {
          strategy,
          displayName,
          description:
            pt?.description ||
            `Client is choosing to participate in the ${displayName} program under this Agreement.`,
        };
      });
  };

  getRiskToleranceOptions = (overrides = {}) => {
    const { strategy, bondType, modelSelection } = { ...this.state, ...overrides };
    if (!strategy) return [];
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    if (!pt) return [];
    const formatActiveIncomeOption = (option) => {
      if (!String(strategy).toLowerCase().includes("active income")) {
        return option;
      }
      if (option === "Conservative") return "Conservative - 4% Income Target";
      if (option === "Moderate") return "Moderate - 5% Income Target";
      return option;
    };
    const formatOptions = (options) => options.map(formatActiveIncomeOption);

    if (this.isExistingRiskToleranceUpdate() || this.isExistingModelChange()) {
      const allBonds = [];
      for (const key of Object.keys(pt)) {
        if (key.endsWith("Bonds") && Array.isArray(pt[key])) {
          allBonds.push(...pt[key]);
        }
      }
      return formatOptions([...new Set(allBonds)]);
    }

    let bonds = [];
    if (pt.taxableBonds && pt.municipalBonds) {
      bonds = bondType === "Municipal" ? pt.municipalBonds : pt.taxableBonds;
    } else if (pt.taxableBonds) {
      bonds = pt.taxableBonds;
    } else if (pt.municipalBonds) {
      bonds = pt.municipalBonds;
    } else {
      for (const key of Object.keys(pt)) {
        if (key.endsWith("Bonds") && Array.isArray(pt[key])) {
          bonds = pt[key];
          break;
        }
      }
    }

    // When a strategy defines modelSelection (e.g. "Program Selection" on Core
    // Bond Strategies), narrow the portfolio list to options matching the chosen
    // model. Convention: model option name (minus a trailing " Strategy") is a
    // prefix of — or equal to — the portfolio name. If no options match, fall
    // back to the unfiltered list.
    if (pt.modelSelection && modelSelection && bonds.length) {
      const narrowed = narrowBondsByModel(bonds, modelSelection);
      if (narrowed.length) return formatOptions(narrowed);
    }

    return formatOptions(bonds);
  };

  getRiskToleranceAliases = () => {
    const pt = this.props.lists?.proposalTypes?.find(
      (p) => p.name === this.state.strategy
    );
    return pt?.riskToleranceAliases || {};
  };

  // Which bond types (Taxable / Municipal) have a portfolio matching the chosen
  // program for the current strategy. Drives the Bond Type lock: when a program
  // only exists under one bond type (e.g. Government Money Market is taxable
  // only), the form forces that bond type and disables the other radio. Data
  // derived — no strategy-name special-casing — so any asymmetric strategy
  // benefits. With no model chosen, every populated bond array is available.
  getAvailableBondTypesForModel = (modelSelection = this.state.modelSelection) => {
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === this.state.strategy);
    if (!pt) return [];
    const has = (bonds) =>
      Array.isArray(bonds) &&
      bonds.length > 0 &&
      (!modelSelection || narrowBondsByModel(bonds, modelSelection).length > 0);
    const types = [];
    if (has(pt.taxableBonds)) types.push("Taxable");
    if (has(pt.municipalBonds)) types.push("Municipal");
    return types;
  };

  // Bond types (Taxable / Municipal) available for an arbitrary strategy by
  // name — used to inspect an Elements row's path target. Unlike
  // getAvailableBondTypesForModel this ignores model selection: the path
  // strategy has no chosen model in this form.
  getBondTypesForStrategyName = (name) => {
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === name);
    if (!pt) return [];
    const types = [];
    if (Array.isArray(pt.taxableBonds) && pt.taxableBonds.length > 0) types.push("Taxable");
    if (Array.isArray(pt.municipalBonds) && pt.municipalBonds.length > 0) types.push("Municipal");
    return types;
  };

  // The path/graduation strategy name configured on the selected strategy
  // row (proposal-types `pathStrategy`), or "" when none.
  getPathStrategyName = () => {
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === this.state.strategy);
    return pt?.pathStrategy || "";
  };

  getPathBondTypes = () => {
    const name = this.getPathStrategyName();
    return name ? this.getBondTypesForStrategyName(name) : [];
  };

  // Show the path bond radio only when the target offers a real choice
  // (both Taxable and Municipal). Taxable-only targets need no selection.
  hasPathBondChoice = () => {
    const types = this.getPathBondTypes();
    return types.includes("Taxable") && types.includes("Municipal");
  };

  // True when the selected strategy is flat AND a path-strategy row exists in
  // the loaded management fees. Used to gate path-stage IAR rendering and
  // validation. Falls back to false during the initial render before
  // managementFees / pathStrategyFees load.
  hasPath = () => {
    const mgmt = this.state.managementFees;
    if (!mgmt || mgmt.flatFee == null) return false;
    if (!this.getPathStrategyName()) return false;
    // The frontend loads the path strategy's mgmt-fees alongside the primary
    // strategy (in the existing fees-fetch path that injected pathMinimumFee).
    // The presence of that fetched row is the source of truth for "path resolves."
    return !!(this.state.managementFees?.pathStrategyRow);
  };

  isFlatFeeOnlyStrategy = () => {
    const mgmt = this.state.managementFees;
    return !!(mgmt && mgmt.flatFee != null && !this.hasPath() && !this.isFusionStrategy());
  };

  isManagedMoneyMarketStrategy = (strategy = this.state.strategy) =>
    String(strategy || "").toLowerCase().includes("managed money market solution");

  getEffectiveIarFeeType = () =>
    this.hasPath() || this.isFlatFeeOnlyStrategy()
      ? this.state.iarFeeType || "Fixed Percentage"
      : this.state.iarFeeType;

  getIarFeeMax = () =>
    this.isManagedMoneyMarketStrategy()
      ? 0.15
      : 2;

  getRiskToleranceDescription = (category) => {
    const BASE_DESCRIPTIONS = {
      Conservative:
        "Emphasis on generating a stable level of current income. Future capital appreciation is a secondary objective. Modest annual principal fluctuation is expected and acceptable.",
      Moderate:
        "Emphasis on both current income and future capital appreciation. Principal risk and fluctuation are expected and acceptable.",
      Growth:
        "Emphasis is entirely on future capital appreciation. Substantial principal risk and fluctuation are expected and acceptable.",
    };

    if (!category) return "";

    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === this.state.strategy);

    // Strategy-level overrides (e.g., Fee-Based Annuity, Core Bond)
    if (pt?.riskToleranceDescriptions?.[category]) {
      return pt.riskToleranceDescriptions[category];
    }

    const base = BASE_DESCRIPTIONS[category] || "";

    // Derive options from the already-found pt to avoid a second .find()
    const options = this.getRiskToleranceOptions();
    if (!options.length) return base;

    // Extract equity allocations for this category from portfolio names like "Conservative 30/70"
    const catLower = category.toLowerCase();
    const allocations = [];
    for (const opt of options) {
      if (!opt.toLowerCase().startsWith(catLower)) continue;
      const match = opt.match(/(\d+)\/(\d+)/);
      if (match) allocations.push(parseInt(match[1], 10));
    }

    if (!allocations.length) return base;

    allocations.sort((a, b) => a - b);
    const pctList = allocations.map((a) => `${a}%`);
    const allocationText = pctList.length === 1
      ? `The portfolio in this category offers an equity allocation of ${pctList[0]}.`
      : `The portfolios in this category offer equity allocations of ${pctList.slice(0, -1).join(", ")}, and ${pctList[pctList.length - 1]} respectively.`;

    return `${base}  ${allocationText}`;
  };

  // --- Section 3: Risk Questionnaire ---
  handleRiskAnswerChange = (section, questionId, score) => {
    this.setState((prev) => {
      const answersKey = `${section}Answers`;
      const answers = { ...prev[answersKey], [questionId]: score };

      const capAnswers = section === "capacity" ? answers : prev.capacityAnswers;
      const willAnswers = section === "willingness" ? answers : prev.willingnessAnswers;
      const { capacityTotal, willingnessTotal, combinedScore, riskCategory } = computeRiskScores(capAnswers, willAnswers);

      let selectedPortfolio = prev.selectedPortfolio;
      if (riskCategory !== prev.riskCategory && !prev.portfolioOverride) {
        selectedPortfolio = "";
      }

      return {
        [answersKey]: answers,
        capacityTotal,
        willingnessTotal,
        combinedScore,
        riskCategory,
        selectedPortfolio,
      };
    });
  };

  handlePortfolioChange = (value) => {
    this.setState({ selectedPortfolio: value });
  };

  handleOverrideToggle = (checked) => {
    this.setState({
      portfolioOverride: checked,
      selectedPortfolio: "",
    });
  };

  handleOverrideReasonChange = (value) => {
    this.setState({ overrideReason: value });
  };

  handleOverrideOtherTextChange = (value) => {
    this.setState({ overrideOtherText: value });
  };

  handleRiskReset = () => {
    this.setState({
      capacityAnswers: {},
      willingnessAnswers: {},
      capacityTotal: 0,
      willingnessTotal: 0,
      combinedScore: 0,
      riskCategory: "",
      selectedPortfolio: "",
      portfolioOverride: false,
      overrideReason: "",
      overrideOtherText: "",
    });
  };

  renderAllocationOnlyRiskSelection = () => {
    const options = this.getRiskToleranceOptions();
    return (
      <div>
        <div className="alert alert-info py-2">
          Model Change requests do not require the Risk Tolerance Questionnaire.
          Select the new allocation below.
        </div>
        <div className="form-group">
          <label htmlFor="modelChangeAllocation">
            <strong>Risk Tolerance / Allocation</strong>
          </label>
          <select
            id="modelChangeAllocation"
            className="form-control"
            value={this.state.selectedPortfolio || ""}
            onChange={(e) => this.handlePortfolioChange(e.target.value)}
            style={{
              borderLeftColor: this.state.selectedPortfolio
                ? "green"
                : "#ced4da",
              borderLeftWidth: "5px",
            }}
          >
            <option value="">Select Allocation</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  // Navigate to proposal step guide with ISP data pre-filled
  navigateToCreateProposal = () => {
    const clientName = this.getCombinedClientName();

    const rawAmount = parseFloat(
      String(this.state.initialInvestmentAmount).replace(/[^0-9.-]+/g, "")
    ) || 0;

    const isFusion = this.state.strategy &&
      this.state.strategy.toLowerCase().includes("fusion");

    const ispProposalData = {
      DateTime: this.state.submissionDateTime || "",
      program: this.state.strategy,
      "CLIENT NAMES": clientName,
      "ADVISOR NAME": this.state.advisorName,
      customTemplate: this.state.advisorName,
      "RISK TOLERANCE": this.state.selectedPortfolio,
      ispRiskCategory: this.state.riskCategory,
      ispSelectedPortfolio: this.state.selectedPortfolio,
      optionsTM: this.state.bondType,
      assetValue: rawAmount > 0 ? `$${rawAmount.toLocaleString()}` : "",
      registrations: this.state.registrations,
      incomeNeeds: "",
      logoSize: "",
      logoOffset: "",
      papercheck: "true",
      othercheck: "",
      insertType: "",
      fusionAccountNumbers: isFusion
        ? this.state.accountNumbers.filter((n) => n.trim())
        : [],
      isNewFusionClient: this.state.requestType === "New",
      ispClients: this.state.jointClients.map((c) => ({
        name: c.clientName,
        isPrimary: c.isPrimary,
      })),
      ispAmount: this.state.initialInvestmentAmount,
      ispGoal: this.getAccountGoalDisplay(),
      ispFusionAllocations: isFusion ? this.state.fusionAllocations : [],
    };

    if (this.props.navigateToProposalFromISP) {
      this.props.navigateToProposalFromISP(ispProposalData);
    } else {
      sessionStorage.setItem("ispProposalData", JSON.stringify(ispProposalData));
      window.location.href = "/proposals/step-guide";
    }
  };

  buildSubmissionPayload = () => {
    const incomeYes = this.state.accountIncomeNeed === "Yes";
    const hasOverride = this.state.portfolioOverride;
    const skipRiskTolerance = this.shouldSkipRiskTolerance();
    const skipRiskQuestionnaireAnswers =
      this.shouldSkipRiskQuestionnaireAnswers();
    const selectedPortfolioCategory = getPortfolioRiskCategory(
      this.state.selectedPortfolio,
      this.getRiskToleranceAliases(),
    );

    // Build risk Q&A array from the standard question sets
    const riskQA = skipRiskQuestionnaireAnswers
      ? []
      : [...CAPACITY_QUESTIONS, ...WILLINGNESS_QUESTIONS].map((q) => {
        const section = q.id.startsWith("cap") ? "capacity" : "willingness";
        const answers = this.state[`${section}Answers`];
        const selectedScore = answers[q.id];
        const selectedAnswer = q.answers.find((a) => a.score === selectedScore);
        return { q: q.text, a: selectedAnswer?.text || "", score: selectedScore };
      });

    // Build risk tolerance description: tied to Risk Allocation (post-override) rather
    // than the questionnaire category, so an overridden portfolio drives the wording.
    // Portfolio names start with the category ("Conservative 30/70", etc.), so the
    // first whitespace-separated token is the category key used by BASE_DESCRIPTIONS.
    const effectiveCategory =
      (this.state.portfolioOverride || this.isExistingModelChange()) &&
      this.state.selectedPortfolio
        ? selectedPortfolioCategory || this.state.selectedPortfolio.split(" ")[0]
        : this.state.riskCategory;
    const riskToleranceDescription = skipRiskTolerance
      ? ""
      : this.getRiskToleranceDescription(effectiveCategory);

    // Final bond-type resolution: when the selected strategy carries
    // setBondValueBlank === true, clear the bond type so it isn't recorded.
    // Any other (falsy) value leaves the chosen bond type untouched.
    const selectedStrategy = this.props.lists?.proposalTypes?.find(
      (p) => p.name === this.state.strategy
    );
    const bondType =
      selectedStrategy?.setBondValueBlank === true ? "" : this.state.bondType;
    const includeFullAccountInfo = !this.isReducedAccountInfoUpdate();
    const includeHouseholding = this.shouldAskHouseholding();
    const ignorePrimaryRegistration = this.isFusionMultiRegistration();
    const effectiveIarFeeType = this.getEffectiveIarFeeType();

    const payload = {
      submissionType: "ISP",
      advisorName: this.state.advisorName,
      custodian: includeFullAccountInfo ? this.state.custodian : "",
      strategy: this.state.strategy,
      strategyDisplayName: this.getStrategyDisplayName(),
      bondType: includeFullAccountInfo ? bondType : "",
      pathStrategy: this.getPathStrategyName(),
      pathBondType:
        includeFullAccountInfo && this.hasPathBondChoice()
          ? this.state.pathBondType
          : "",
      // Path-stage IAR fee — only meaningful when path-mode is active. Sending
      // these unconditionally is fine; the Lambda only consumes them when the
      // mgmt-fee row has flatFee AND the path row exists.
      pathIarFeeType: this.state.pathIarFeeType,
      pathIarFeeFlat: this.state.pathIarFeeFlat,
      pathIarFeeTiered: this.state.pathIarFeeTiered,
      modelSelection: includeFullAccountInfo ? this.state.modelSelection : "",
      registration: this.getRegistrationDisplay(),
      registrations: ignorePrimaryRegistration ? [] : this.state.registrations,
      fusionRegistrationType: this.isFusionStrategy()
        ? this.state.fusionRegistrationType || "Single"
        : "",
      fusionRebalanceType: this.isFusionStrategy()
        ? this.getFusionRebalanceType()
        : "",
      requestType: this.state.requestType,
      existingRequestAction: this.state.existingRequestAction,
      accountNumber: this.allowsMultipleAccountNumbers()
        ? (this.state.accountNumbers || []).filter((n) => n.trim()).join(", ")
        : this.state.accountNumber,
      clientProfileImaSubmitted: this.shouldAskClientProfileIma()
        ? this.state.clientProfileImaSubmitted
        : "",
      // T19 — when householding is disallowed for the strategy, omit
      // household values from the payload so the PDF / DynamoDB don't record
      // a Yes/No response for a strategy that can't household. Deferred
      // strategies still collect and render Yes/NA after account graduation.
      householding:
        includeHouseholding
          ? this.state.householding
          : "",
      householdingMode: this.getStrategyHouseholdingMode(),
      householdRefName:
        includeHouseholding &&
        this.state.householding === "Yes"
          ? this.state.householdRefName
          : "",
      householdRefLast4:
        includeHouseholding &&
        this.state.householding === "Yes"
          ? this.state.householdRefLast4
          : "",
      // householdRef preserved for backwards compatibility with older
      // submissions; new submissions always populate the split fields above.
      householdRef:
        includeHouseholding &&
        this.state.householding === "Yes"
          ? this.state.householdRefLast4 || this.state.householdRef
          : "",
      householdReason:
        includeHouseholding &&
        this.state.householding === "No"
          ? this.getHouseholdReasonText()
          : "",
      householdReasonType: includeHouseholding ? this.state.householdReasonType : "",
      householdReasonOther: includeHouseholding ? this.state.householdReasonOther : "",
      initialInvestmentAmount: includeFullAccountInfo
        ? this.state.initialInvestmentAmount
        : "",
      accountNetWorthPct: includeFullAccountInfo
        ? this.state.accountNetWorthPct
        : "",
      accountIncomeNeed: includeFullAccountInfo
        ? this.state.accountIncomeNeed
        : "",
      accountIncomeAmount:
        includeFullAccountInfo && incomeYes ? this.state.accountIncomeAmount : "",
      accountIncomeStartDate:
        includeFullAccountInfo && incomeYes
          ? this.state.accountIncomeStartDate
          : "",
      accountIncomeEndDate:
        includeFullAccountInfo && incomeYes ? this.state.accountIncomeEndDate : "",
      accountIncomeFrequency:
        includeFullAccountInfo && incomeYes
          ? this.state.accountIncomeFrequency
          : "",
      accountGoal: includeFullAccountInfo ? this.getAccountGoalForPayload() : "",
      accountGoalOther:
        includeFullAccountInfo ? this.getAccountGoalOtherForPayload() : "",
      sourceOfFunds: includeFullAccountInfo ? this.state.sourceOfFunds : [],
      sourceOfFundsText: includeFullAccountInfo ? this.state.sourceOfFundsText : {},
      soloKQuestionnaire: this.hasSoloKRegistration()
        ? {
          ...getEmptySoloKQuestionnaire(),
          ...this.state.soloKQuestionnaire,
          ownershipEvidence: this.state.soloKQuestionnaire?.ownershipEvidence || [],
        }
        : undefined,
      // T15 — only ship UGMA/UTMA fields when applicable; backend uses these
      // to drive a conditional row on the PDF.
      minorName:
        includeFullAccountInfo && this.isUgmaUtmaRegistration()
          ? this.state.minorName
          : "",
      relationshipToCustodian:
        includeFullAccountInfo && this.isUgmaUtmaRegistration()
        ? this.state.relationshipToCustodian
        : "",
      businessName:
        includeFullAccountInfo && this.isBusinessRegistration()
          ? this.state.businessName
          : "",
      businessEinLast4:
        includeFullAccountInfo && this.isBusinessRegistration()
        ? this.state.businessEinLast4
        : "",
      jointClients: this.getOrderedJointClients(),
      riskScores: skipRiskTolerance
        ? {}
        : this.isExistingModelChange()
        ? {
          capacityTotal: "",
          willingnessTotal: "",
          combinedScore: "",
          riskCategory: selectedPortfolioCategory || effectiveCategory || "",
          selectedPortfolio: this.state.selectedPortfolio,
          portfolioOverride: false,
          overrideReason: "",
          overrideOtherText: "",
          capacityAnswers: {},
          willingnessAnswers: {},
        }
        : {
          capacityTotal: this.state.capacityTotal,
          willingnessTotal: this.state.willingnessTotal,
          combinedScore: this.state.combinedScore,
          riskCategory: this.state.riskCategory,
          selectedPortfolio: this.state.selectedPortfolio,
          portfolioOverride: this.state.portfolioOverride,
          overrideReason: hasOverride ? this.state.overrideReason : "",
          overrideOtherText: hasOverride && this.state.overrideReason === "Other" ? this.state.overrideOtherText : "",
          capacityAnswers: this.state.capacityAnswers,
          willingnessAnswers: this.state.willingnessAnswers,
        },
      iarFeeTier: this.state.iarFeeTier,
      managementFeeTierKey: this.getSelectedManagementFeeTierKey(),
      iarFeeType: effectiveIarFeeType,
      iarFeeFlat: this.state.iarFeeFlat,
      iarFeeTiered: this.state.iarFeeTiered,
      iarFeeScheduleType: this.state.iarFeeScheduleType,
      iarFeeWaiver: this.state.iarFeeWaiver,
      fusionAllocations: this.isFusionStrategy() ? this.state.fusionAllocations : undefined,
      fusionStrategyDescriptions: this.getFusionStrategyDescriptions(),
      // T4 — per-sleeve schedules replace the blended computation. The
      // payload variant ships the FULL eligible-Fusion-sub-strategy list
      // (with weight only on selected allocations) so the PDF can render
      // every eligible sub-strategy's tier schedule for compliance.
      fusionFeeBlocks: this.isFusionStrategy() ? this.buildFusionFeeBlocksForPayload() : undefined,
      riskQA,
      riskToleranceDescription,
      additionalAdvisors: this.state.additionalAdvisors,
      accountNumbers: this.state.accountNumbers,
      advisors: [
        { name: this.state.advisorName, isPrimary: true },
        ...this.state.additionalAdvisors.filter((n) => n).map((name) => ({ name, isPrimary: false })),
      ],
    };
    if (!payload.soloKQuestionnaire) delete payload.soloKQuestionnaire;
    return payload;
  };

  // --- Section 5: Submit ---
  handleSubmit = async () => {
    if (!this.isFormValid() || this.state.isSubmitting) return;

    const now = new Date();
    const submissionDateTime = this.state.proposalDateTime || now.toUTCString();
    const localDateTime = now.toLocaleString();
    this.setState({ isSubmitting: true, submissionDateTime });

    const payload = this.buildSubmissionPayload();

    try {
      console.log("ISP Submission Payload:", JSON.stringify(payload, null, 2));

      // Get current user for context
      let userId = "";
      try {
        const currentUser = await getCurrentUser();
        userId = currentUser.username || currentUser.userId || "";
      } catch (e) {
        console.log("Could not get current user:", e);
      }

      const authHeaders = await getAuthHeaders();
      const apiName = "vc-proposal-generator";
      const path = "/proposal-generator/isp";

      // Send data flat in body — API Gateway mapping template wraps it
      // in "body-json" and adds "context" (user-id from Cognito) automatically.
      // Same pattern as proposalConfirm.jsx postData().
      await post({
        apiName,
        path,
        options: {
          headers: {
            ...authHeaders,
          },
          body: {
            ...payload,
            "date-time": submissionDateTime,
            "localDateTime": localDateTime,
            "user-name": userId,
            selectedPortfolio: this.state.selectedPortfolio,
            generateProposal: this.state.generateProposal || false,
            proposalName: this.state.proposalName || "",
            strategyDescription: this.getStrategyDescription(),
          },
        },
      }).response;

      this.setState({ isSubmitting: false, showSuccess: true });
    } catch (error) {
      console.error("ISP submission error:", error);
      // Still show success if the error is non-fatal (record may have been saved)
      // In production, check if the DynamoDB write succeeded before showing success
      this.setState({
        isSubmitting: false,
        submitError: error.message || "An error occurred while submitting. Please try again.",
      });
    }
  };

  handleCreateAnother = () => {
    this.setState({
      expandedSection: "1",
      advisorName: "",
      additionalAdvisors: [],
      strategy: "",
      bondType: "Taxable",
      pathBondType: "",
      modelSelection: "",
      requestType: "",
      existingRequestAction: "",
      accountNumber: "",
      accountNumbers: [""],
      clientProfileImaSubmitted: "",
      householding: "",
      householdRefName: "",
      householdRefLast4: "",
      householdRef: "",
      householdReason: "",
      householdReasonType: "",
      householdReasonOther: "",
      registrations: [
        { registration: "registration0", value: "", percentage: "100" },
      ],
      initialInvestmentAmount: "",
      accountNetWorthPct: "",
      accountIncomeNeed: "",
      accountIncomeAmount: "",
      accountIncomeStartDate: "",
      accountIncomeEndDate: "",
      accountIncomeFrequency: "",
      accountGoal: "",
      accountGoalOther: "",
      sourceOfFunds: [],
      sourceOfFundsText: {},
      soloKQuestionnaire: getEmptySoloKQuestionnaire(),
      minorName: "",
      relationshipToCustodian: "",
      businessName: "",
      businessEinLast4: "",
      fusionRegistrationType: "Single",
      fusionRebalanceType: "Automatic",
      jointClients: [],
      showClientDialog: false,
      dialogEditIndex: -1,
      dialogCopySource: -1,
      capacityAnswers: {},
      willingnessAnswers: {},
      capacityTotal: 0,
      willingnessTotal: 0,
      combinedScore: 0,
      riskCategory: "",
      selectedPortfolio: "",
      portfolioOverride: false,
      overrideReason: "",
      overrideOtherText: "",
      managementFees: null,
      managementFeesLoading: false,
      iarFeeTier: "",
      selectedManagementFeeTierKey: "",
      availableManagementFeeTiers: [],
      availableManagementFeeTierOptions: [],
      iarFeeType: "",
      iarFeeFlat: "",
      iarFeeTiered: [],
      pathIarFeeType: "Fixed Percentage",
      pathIarFeeFlat: "",
      pathIarFeeTiered: [],
      iarFeeScheduleType: "Schedule 1",
      iarFeeWaiver: null,
      fusionAllocations: [],
      fusionSubFees: {},
      fusionSubFeesLoading: false,
      fusionFeeValidation: { isValid: true, issues: [] },
      feesSectionVisited: false,
      showZeroFeeModal: false,
      pendingSection: null,
      isSubmitting: false,
      showSuccess: false,
    });
  };

  // --- Validation ---
  isSection1Valid = () => {
    const {
      advisorName,
      clientProfileImaSubmitted,
      householding,
      requestType,
      accountNumber,
      householdRefName,
      householdRefLast4,
      householdReasonType,
      householdReasonOther,
      jointClients,
    } = this.state;
    if (!advisorName) return false;
    if (!requestType) return false;
    if (this.shouldAskClientProfileIma() && !clientProfileImaSubmitted) return false;
    if (requestType === "Existing" && !this.state.existingRequestAction) return false;
    // T19 — when householding is disallowed for the selected strategy, skip
    // the household-required checks entirely. Deferred-with-note strategies
    // still collect Yes/NA for the post-graduation householding section.
    if (this.shouldAskHouseholding()) {
      if (!householding) return false;
      if (householding === "Yes") {
        if (!householdRefName) return false;
        if (!householdRefLast4 || householdRefLast4.length < 4) return false;
      }
      if (householding === "No") {
        if (!householdReasonType) return false;
        if (householdReasonType === "Other" && !householdReasonOther) return false;
      }
    }
    if (requestType === "Existing") {
      if (this.allowsMultipleAccountNumbers()) {
        const filled = (this.state.accountNumbers || []).filter(
          (n) => (n || "").trim() !== "",
        );
        if (filled.length === 0) return false;
        if (filled.some((n) => n.trim().length < 4)) return false;
      } else if (!accountNumber || accountNumber.length < 4) {
        return false;
      }
    }
    if (jointClients.length === 0) return false;
    // Exactly one client must be flagged primary — we no longer auto-assign it.
    const primary = jointClients.find((c) => c.isPrimary);
    if (!primary) return false;
    // T10 — primary client must include last-4 of SSN
    if (!primary.ssnLast4 || primary.ssnLast4.length < 4) return false;
    return true;
  };

  // Mirrors ispAccountInfo.getStrategyLimits — fusion strategies use
  // fusionLimits, others use limits. Returns null when no limits are set so
  // the validator skips the range check entirely.
  getStrategyInvestmentLimits = () => {
    const { strategy } = this.state;
    if (!strategy) return null;
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    if (!pt) return null;
    const isFusion = strategy.toLowerCase().includes("fusion");
    const limitsToUse = isFusion ? pt.fusionLimits || pt.limits : pt.limits;
    if (!limitsToUse) return null;
    const min = Number(limitsToUse.min);
    const max = Number(limitsToUse.max);
    if (isNaN(min) && isNaN(max)) return null;
    return { min: isNaN(min) ? 0 : min, max: isNaN(max) ? Infinity : max };
  };

  // Validate one Fusion sub-strategy allocation's dollar amount against that
  // sub-strategy's investment limits (fusionLimits, falling back to limits).
  // The amount is the Section 2 "Initial Amount of Investment" scaled by the
  // allocation percentage. Returns an error string, or null when the
  // allocation is within range, has no amount yet, or the sub-strategy has no
  // configured limits.
  getFusionAllocationLimitError = (allocation) => {
    const strategy = String(allocation?.strategy || "").trim();
    const percentage = parseFloat(allocation?.percentage);
    if (!strategy || !Number.isFinite(percentage) || percentage <= 0) return null;

    const investment = parseFloat(
      String(this.state.initialInvestmentAmount).replace(/[^0-9.-]+/g, "")
    );
    if (!Number.isFinite(investment) || investment <= 0) return null;

    const amount = Math.round((investment * percentage) / 100);
    if (amount <= 0) return null;

    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    const limitsToUse = pt?.fusionLimits || pt?.limits;
    if (!limitsToUse) return null;

    const min = Number(limitsToUse.min);
    const max = Number(limitsToUse.max);
    const fmt = (value) =>
      value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
      });

    if (!isNaN(min) && amount < min) {
      return `Allocated ${fmt(amount)} is below the ${fmt(min)} minimum for this sub-strategy.`;
    }
    if (!isNaN(max) && amount > max) {
      return `Allocated ${fmt(amount)} exceeds the ${fmt(max)} maximum for this sub-strategy.`;
    }
    return null;
  };

  getModelSelectionConfig = () => {
    const { strategy } = this.state;
    if (!strategy) return null;
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    return pt?.modelSelection || null;
  };

  getHouseholdReasonText = () => {
    if (this.state.householdReasonType === "Only account") {
      return "This is the only account in the household.";
    }
    if (this.state.householdReasonType === "Other") {
      return this.state.householdReasonOther || "";
    }
    return this.state.householdReason || "";
  };

  isExistingFeeUpdate = () =>
    this.state.requestType === "Existing" &&
    this.state.existingRequestAction === "Fee Update";

  isExistingRiskToleranceUpdate = () =>
    this.state.requestType === "Existing" &&
    this.state.existingRequestAction === "Risk Tolerance Update";

  isExistingModelChange = () =>
    this.state.requestType === "Existing" &&
    this.state.existingRequestAction === "Model Change";

  shouldAskClientProfileIma = () =>
    this.state.requestType === "New" ||
    (this.state.requestType === "Existing" &&
      this.state.existingRequestAction === "Strategy Change");

  shouldAskHouseholding = () =>
    this.state.requestType === "New" &&
    this.getStrategyHouseholdingMode() !== "disallowed";

  getAccountGoalSelections = () => {
    if (Array.isArray(this.state.accountGoal)) return this.state.accountGoal;
    return this.state.accountGoal ? [this.state.accountGoal] : [];
  };

  isOtherAccountGoalSelected = () =>
    this.isManagedMoneyMarketStrategy()
      ? this.getAccountGoalSelections().includes("Other")
      : this.state.accountGoal === "Funding for another goal";

  getAccountGoalForPayload = () => {
    if (this.isManagedMoneyMarketStrategy()) {
      return this.getAccountGoalSelections().join(", ");
    }
    return this.state.accountGoal || "";
  };

  getAccountGoalOtherForPayload = () =>
    this.isOtherAccountGoalSelected() ? this.state.accountGoalOther : "";

  getAccountGoalDisplay = () => {
    if (this.isManagedMoneyMarketStrategy()) {
      const parts = this.getAccountGoalSelections().map((goal) =>
        goal === "Other" && this.state.accountGoalOther
          ? `Other: ${this.state.accountGoalOther}`
          : goal,
      );
      return parts.join(", ");
    }
    return this.state.accountGoal === "Funding for another goal"
      ? this.state.accountGoalOther || "Funding for another goal"
      : this.state.accountGoal;
  };

  isReducedAccountInfoUpdate = () =>
    this.state.requestType === "Existing" &&
    this.isReducedExistingAccountAction(this.state.existingRequestAction);

  isFusionMultiRegistration = () =>
    this.isFusionStrategy() && this.state.fusionRegistrationType === "Multi";

  getFusionRebalanceType = () =>
    this.isFusionMultiRegistration()
      ? "Manual"
      : this.state.fusionRebalanceType || "Automatic";

  shouldSkipRiskTolerance = () => this.isExistingFeeUpdate();

  shouldSkipRiskQuestionnaireAnswers = () =>
    this.isExistingFeeUpdate() || this.isExistingModelChange();

  allowsMultipleAccountNumbers = () =>
    this.isFusionStrategy() || this.isExistingFeeUpdate();

  getRegistrationDisplay = () => {
    if (this.isFusionMultiRegistration()) return "";
    const values = (this.state.registrations || [])
      .map((registration) => registration.value)
      .filter(Boolean);
    return values.join(", ");
  };

  hasSoloKRegistration = (registrations = this.state.registrations) => {
    if (this.isFusionMultiRegistration() && registrations === this.state.registrations) {
      return false;
    }
    return (registrations || []).some((registration) =>
      isSoloKRegistrationValue(registration?.value)
    );
  };

  isSoloKQuestionnaireValid = () => {
    const q = {
      ...getEmptySoloKQuestionnaire(),
      ...this.state.soloKQuestionnaire,
    };
    if (!q.sponsoringBusinessName.trim()) return false;
    if (!q.ownerCount || Number(q.ownerCount) < 1) return false;
    if (q.employeeCount === "" || Number(q.employeeCount) < 0) return false;
    if (!q.anticipatesEmployees) return false;
    if (!q.planType) return false;
    if (q.planType === "Existing Plan" && !q.existingPlanDocumentsProvided) return false;
    if (!Array.isArray(q.ownershipEvidence) || q.ownershipEvidence.length === 0) return false;
    if (!q.certificationNoEmployees) return false;
    if (!q.certificationContributionLimits) return false;
    return true;
  };

  // T19 — read the per-strategy householding mode from proposal-types.
  // Possible values:
  //   - "allowed" (default): current behavior; Yes/No prompt + conditional fields.
  //   - "disallowed": hide the householding section entirely (e.g., Managed
  //     Money Market Solutions). The PDF also omits household placeholders.
  //   - "deferred-with-note": show the Yes/NA prompt with a static note
  //     (e.g., Elements: "Householding will not apply until account graduates
  //     to selected path."). PDF labels the result after account graduation.
  getStrategyHouseholdingMode = () => {
    const strategy = this.state.strategy;
    if (!strategy) return "allowed";
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    const mode = pt?.householdingMode;
    if (mode === "disallowed" || mode === "deferred-with-note") return mode;
    return "allowed";
  };

  // T15 — true when the primary registration is UGMA or UTMA. Used by both
  // the form (to surface conditional minor-name + relationship inputs) and
  // by validation/payload to require/serialize them.
  isUgmaUtmaRegistration = () => {
    if (this.isFusionMultiRegistration()) return false;
    const value = this.state.registrations?.[0]?.value || "";
    if (!value) return false;
    const upper = value.toUpperCase();
    return upper.includes("UGMA") || upper.includes("UTMA");
  };

  isBusinessRegistration = () => {
    if (this.isFusionMultiRegistration()) return false;
    const value = this.state.registrations?.[0]?.value || "";
    if (!value) return false;
    const regType = this.props.lists?.registrationTypes?.find((r) => r.name === value);
    return regType?.category === "Business Account";
  };

  isSection2Valid = () => {
    const {
      strategy,
      registrations,
      sourceOfFunds,
      initialInvestmentAmount,
      accountNetWorthPct,
      accountIncomeNeed,
      accountIncomeAmount,
      accountIncomeStartDate,
      accountIncomeFrequency,
      accountGoal,
      accountGoalOther,
    } = this.state;
    if (!strategy) return false;
    if (!this.isFusionMultiRegistration() && !registrations[0]?.value) return false;
    if (this.hasSoloKRegistration() && !this.isSoloKQuestionnaireValid()) return false;
    if (this.isReducedAccountInfoUpdate()) {
      if (this.isFusionMultiRegistration()) return true;
      return this.isExistingFeeUpdate()
        ? registrations.every((registration) => !!registration.value)
        : true;
    }
    const modelConfig = this.getModelSelectionConfig();
    if (modelConfig && !this.state.modelSelection) return false;
    // T15 — UGMA/UTMA registrations require minor name + relationship.
    if (this.isUgmaUtmaRegistration()) {
      if (!this.state.minorName) return false;
      if (!this.state.relationshipToCustodian) return false;
    }
    if (this.isBusinessRegistration()) {
      if (!this.state.businessName) return false;
      if (!/^\d{4}$/.test(String(this.state.businessEinLast4 || ""))) return false;
    }
    // Custodian is required when the custodians list is populated (matches the
    // UI green/grey indicator on the custodian dropdown).
    const custodians = this.props.lists?.custodians;
    if (Array.isArray(custodians) && custodians.length > 0 && !this.state.custodian) return false;
    // Bond type required when the strategy + registration combo offers a choice.
    if (this.hasBondOptions() && !this.state.bondType) return false;
    if (sourceOfFunds.length === 0) return false;
    if (sourceOfFunds.includes("Gift From Immediate Relative") && !this.state.sourceOfFundsText["Gift From Immediate Relative"]?.trim()) return false;
    if (sourceOfFunds.includes("Other") && !this.state.sourceOfFundsText["Other"]?.trim()) return false;
    // The asterisked sources (Insurance/Annuity, Mutual Funds/ETFs/UITs) share
    // a single advisorSoldFunds disclosure text — required if either is picked.
    const needsAdvisorSold =
      sourceOfFunds.includes("Insurance/Annuity Contract") ||
      sourceOfFunds.includes("Mutual Funds/ETFs/UITs");
    if (needsAdvisorSold && !this.state.sourceOfFundsText["advisorSoldFunds"]?.trim()) return false;
    if (!initialInvestmentAmount) return false;
    // Enforce strategy min/max on the investment amount — the UI shows a red
    // border when out of range, but until now nothing blocked submission.
    const investmentNum =
      parseFloat(String(initialInvestmentAmount).replace(/[^0-9.-]+/g, "")) || 0;
    const limits = this.getStrategyInvestmentLimits();
    if (limits) {
      if (investmentNum < limits.min) return false;
      if (limits.max !== Infinity && investmentNum > limits.max) return false;
    }
    if (!accountNetWorthPct) return false;
    if (accountIncomeNeed === "Yes") {
      if (!accountIncomeAmount) return false;
      const incomeNum = parseFloat(String(accountIncomeAmount).replace(/[^0-9.-]+/g, "")) || 0;
      const investmentNum = parseFloat(String(initialInvestmentAmount).replace(/[^0-9.-]+/g, "")) || 0;
      if (investmentNum > 0 && incomeNum > investmentNum) return false;
      if (!accountIncomeStartDate) return false;
      const todayIso = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .split("T")[0];
      if (accountIncomeStartDate < todayIso) return false;
      if (this.state.accountIncomeEndDate && this.state.accountIncomeEndDate <= accountIncomeStartDate) return false;
      if (!accountIncomeFrequency) return false;
    }
    if (this.isManagedMoneyMarketStrategy()) {
      const goals = this.getAccountGoalSelections();
      if (goals.length === 0) return false;
      if (goals.includes("Other") && !accountGoalOther) return false;
    } else {
      if (!accountGoal) return false;
      if (accountGoal === "Funding for another goal" && !accountGoalOther) return false;
    }
    return true;
  };

  isSection3Valid = () => {
    if (this.shouldSkipRiskTolerance()) return true;
    if (this.isExistingModelChange()) return !!this.state.selectedPortfolio;
    const {
      capacityAnswers, willingnessAnswers, selectedPortfolio,
      portfolioOverride, overrideReason, overrideOtherText,
    } = this.state;
    const capAnswered = CAPACITY_QUESTIONS.filter((q) => capacityAnswers[q.id] !== undefined).length;
    const willAnswered = WILLINGNESS_QUESTIONS.filter((q) => willingnessAnswers[q.id] !== undefined).length;
    if (capAnswered < CAPACITY_QUESTIONS.length) return false;
    if (willAnswered < WILLINGNESS_QUESTIONS.length) return false;
    if (!selectedPortfolio) return false;
    if (
      !portfolioOverride &&
      !portfolioMatchesRiskCategory(
        selectedPortfolio,
        this.state.riskCategory,
        this.getRiskToleranceAliases(),
      )
    ) {
      return false;
    }
    if (portfolioOverride) {
      if (!overrideReason) return false;
      if (overrideReason === "Other" && !overrideOtherText) return false;
    }
    return true;
  };

  isSection4Valid = () => {
    if (!this.state.feesSectionVisited) return false;
    const iarFeeMax = this.getIarFeeMax();
    const exceedsIarFeeMax = (value) =>
      value !== "" &&
      value !== undefined &&
      value !== null &&
      !Number.isNaN(Number(value)) &&
      Number(value) > iarFeeMax;
    const isIarFeeComplete = (type, flat, tiered) => {
      const effectiveType = type || "Fixed Percentage";
      if (effectiveType === "Fixed Percentage") {
        if (flat === "" || flat === undefined) return false;
        if (exceedsIarFeeMax(flat)) return false;
        if (parseFloat(flat) === 0 && !this.state.iarFeeWaiver) return false;
        return true;
      }
      if (effectiveType === "Tiered") {
        const labels = this.getFeeScheduleLabels();
        const tieredRates = normalizeIarFeeTiered(tiered, labels);
        const allFilled = tieredRates.every((t) => t.rate !== undefined && t.rate !== "");
        if (!allFilled) return false;
        if (tieredRates.some((t) => exceedsIarFeeMax(t.rate))) return false;
        const hasZero = tieredRates.some((t) => parseFloat(t.rate) === 0);
        if (hasZero && !this.state.iarFeeWaiver) return false;
        return true;
      }
      return false;
    };

    if (this.isFusionStrategy()) {
      const { fusionAllocations, iarFeeTier, fusionFeeValidation } = this.state;
      if (!iarFeeTier) return false;
      if (fusionAllocations.length === 0) return false;
      if (
        fusionAllocations.some(
          (a) =>
            !a.strategy ||
            !a.riskTolerance ||
            !a.taxSensitivity ||
            (this.isFusionMultiRegistration() &&
              (!a.registration || !a.accountOwner)) ||
            !a.percentage ||
            parseFloat(a.percentage) <= 0
        )
      ) return false;
      // Lenient sub-strategy fee lookup: management-fees rows in DynamoDB may
      // be keyed by the IM tier alone ("Standard") while the form's selected
      // tier carries an IAR-schedule suffix ("Standard WK"). resolveFusionSubFees
      // tries the exact selected tier, the collapsed label, then any cached
      // entry for the strategy. Matches buildFusionFeeBlocks / fetchFusionSubFees.
      const exactTier = this.getSelectedManagementFeeTierKey();
      const collapsed = collapseTierLabel(exactTier);
      if (fusionAllocations.some((a) => !this.resolveFusionSubFees(a.strategy, exactTier, collapsed))) return false;
      if (!fusionFeeValidation?.isValid) return false;
      const total = fusionAllocations.reduce((sum, a) => sum + (parseFloat(a.percentage) || 0), 0);
      if (Math.abs(total - 100) > 0.01) return false;
      // Each sub-strategy's allocated dollar amount must fall within its
      // investment limits.
      if (fusionAllocations.some((a) => this.getFusionAllocationLimitError(a))) return false;
    } else {
      if (!this.state.iarFeeTier) return false;
    }

    // Fee value must be entered — zero requires a waiver.
    if (this.hasPath()) {
      if (!isIarFeeComplete(
        this.getEffectiveIarFeeType(),
        this.state.iarFeeFlat,
        this.state.iarFeeTiered,
      )) return false;
    } else {
      const effectiveIarFeeType = this.getEffectiveIarFeeType();
      if (!effectiveIarFeeType) return false;
      if (!isIarFeeComplete(
        effectiveIarFeeType,
        this.state.iarFeeFlat,
        this.state.iarFeeTiered,
      )) return false;
    }
    if (this.hasPath()) {
      if (!this.state.pathIarFeeType) return false;
      if (!isIarFeeComplete(
        this.state.pathIarFeeType,
        this.state.pathIarFeeFlat,
        this.state.pathIarFeeTiered,
      )) return false;
    }
    return true;
  };

  isSection5Valid = () => {
    return (
      this.isSection1Valid() &&
      this.isSection2Valid() &&
      (this.shouldSkipRiskTolerance() || this.isSection3Valid()) &&
      this.isSection4Valid() &&
      this.getFusionAllocationRiskValidation().isValid
    );
  };

  isFormValid = () => {
    return this.isSection5Valid();
  };

  render() {
    const { expandedSection } = this.state;

    // Build values objects for child components
    const householdingMode = this.getStrategyHouseholdingMode();
    const section1Values = {
      advisorName: this.state.advisorName,
      strategy: this.state.strategy,
      requestType: this.state.requestType,
      existingRequestAction: this.state.existingRequestAction,
      accountNumber: this.state.accountNumber,
      accountNumbers: this.state.accountNumbers,
      clientProfileImaSubmitted: this.state.clientProfileImaSubmitted,
      householding: this.state.householding,
      householdRefName: this.state.householdRefName,
      householdRefLast4: this.state.householdRefLast4,
      householdReason: this.state.householdReason,
      householdReasonType: this.state.householdReasonType,
      householdReasonOther: this.state.householdReasonOther,
      registrations: this.state.registrations,
      // T19 — branches the Householding UI by per-strategy DynamoDB flag.
      householdingMode,
    };

    const section2Values = {
      advisorName: this.state.advisorName,
      additionalAdvisors: this.state.additionalAdvisors,
      existingRequestAction: this.state.existingRequestAction,
      custodian: this.state.custodian,
      strategy: this.state.strategy,
      fusionRegistrationType: this.state.fusionRegistrationType || "Single",
      fusionRebalanceType: this.getFusionRebalanceType(),
      bondType: this.state.bondType,
      showPathBondType: this.hasPathBondChoice(),
      pathBondType: this.state.pathBondType,
      pathStrategyName: this.getPathStrategyName(),
      modelSelection: this.state.modelSelection,
      initialInvestmentAmount: this.state.initialInvestmentAmount,
      accountNetWorthPct: this.state.accountNetWorthPct,
      accountIncomeNeed: this.state.accountIncomeNeed,
      accountIncomeAmount: this.state.accountIncomeAmount,
      accountIncomeStartDate: this.state.accountIncomeStartDate,
      accountIncomeEndDate: this.state.accountIncomeEndDate,
      accountIncomeFrequency: this.state.accountIncomeFrequency,
      accountGoal: this.state.accountGoal,
      accountGoalOther: this.state.accountGoalOther,
      sourceOfFunds: this.state.sourceOfFunds,
      sourceOfFundsText: this.state.sourceOfFundsText,
      isSoloKRegistration: this.hasSoloKRegistration(),
      soloKQuestionnaire: this.state.soloKQuestionnaire,
      // T15 — surfaced/required only when registrations[0] is UGMA/UTMA.
      isUgmaUtma: this.isUgmaUtmaRegistration(),
      minorName: this.state.minorName,
      relationshipToCustodian: this.state.relationshipToCustodian,
      isBusinessRegistration: this.isBusinessRegistration(),
      businessName: this.state.businessName,
      businessEinLast4: this.state.businessEinLast4,
      // T19 — surfaced alongside the strategy picker so the user learns how
      // the selected strategy affects Householding (which lives back in
      // Section 1, before the strategy is chosen).
      householdingMode: this.getStrategyHouseholdingMode(),
    };

    return (
      <div>
        <h1 style={{ display: "inline-block" }}>
          Create Investment Strategy Plan and Fee Disclosure (ISP)
        </h1>
        <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
          All fields are required unless marked <em>(optional)</em>.
          Fields turn <span style={{ color: "green", fontWeight: "bold" }}>green</span> when completed.
        </p>

        {this.state.proposalPrefillInfo && (
          <div className="alert alert-info alert-dismissible fade show" role="alert">
            <strong>Pre-filled from Proposal:</strong> {this.state.proposalPrefillInfo}
            <button
              type="button"
              className="close"
              aria-label="Close"
              onClick={() => this.setState({ proposalPrefillInfo: "" })}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        )}

        <CollapsibleSection
          sectionNumber="1"
          title="Client Information"
          isExpanded={expandedSection === "1"}
          isValid={this.isSection1Valid()}
          onToggle={() => this.toggleSection("1")}
        >
          <ISPClientProfile
            values={section1Values}
            onChange={this.handleFieldChange}
            lists={this.props.lists}
            onAccountNumberChange={this.handleAccountNumberChange}
            jointClients={this.state.jointClients}
            showClientDialog={this.state.showClientDialog}
            dialogEditIndex={this.state.dialogEditIndex}
            dialogCopySource={this.state.dialogCopySource}
            onAddClient={this.showAddClientDialog}
            onEditClient={this.showEditClientDialog}
            onCloseDialog={this.hideClientDialog}
            onSaveClient={this.handleSaveClient}
            onCopyClient={this.copyClient}
            onRemoveClient={this.removeClient}
            clientNameHistory={this.getClientNameHistory()}
            additionalAdvisors={this.state.additionalAdvisors}
            onAddAdvisor={() => this.setState((s) => ({ additionalAdvisors: [...s.additionalAdvisors, ""] }))}
            onChangeAdvisor={(index, value) => {
              this.setState((s) => {
                const updated = [...s.additionalAdvisors];
                updated[index] = value;
                return { additionalAdvisors: updated };
              });
            }}
            onRemoveAdvisor={(index) => {
              this.setState({
                confirmModal: {
                  title: "Remove IAR",
                  message: `Are you sure you want to remove this IAR?`,
                  onConfirm: () => this.setState((s) => ({
                    additionalAdvisors: s.additionalAdvisors.filter((_, i) => i !== index),
                    confirmModal: null,
                  })),
                },
              });
            }}
          />
        </CollapsibleSection>

        <CollapsibleSection
          sectionNumber="2"
          title="Account Information"
          isExpanded={expandedSection === "2"}
          isValid={this.isSection2Valid()}
          onToggle={() => this.toggleSection("2")}
        >
          <ISPAccountInfo
            values={section2Values}
            onChange={this.handleFieldChange}
            lists={this.props.lists}
            registrations={this.state.registrations}
            onRegistrationChange={this.handleRegistrationChange}
            onAddRegistration={this.addRegistration}
            onRemoveRegistration={this.removeRegistration}
            onSoloKChange={this.handleSoloKQuestionnaireChange}
            onSoloKOwnershipEvidenceToggle={this.handleSoloKOwnershipEvidenceToggle}
            onSourceOfFundsToggle={this.handleSourceOfFundsToggle}
            onSourceOfFundsTextChange={this.handleSourceOfFundsTextChange}
            showBondType={this.hasBondOptions()}
            availableBondTypes={this.getAvailableBondTypesForModel()}
            modelSelectionConfig={this.getModelSelectionConfig()}
          />
        </CollapsibleSection>

        <CollapsibleSection
          sectionNumber="3"
          title="Risk Tolerance Questionnaire"
          isExpanded={expandedSection === "3"}
          isValid={this.isSection3Valid()}
          onToggle={() => this.toggleSection("3")}
        >
          {this.shouldSkipRiskTolerance() ? (
            <div className="alert alert-info mb-0">
              Risk Tolerance Questionnaire is not required for Fee Update
              requests.
            </div>
          ) : this.isExistingModelChange() ? (
            this.renderAllocationOnlyRiskSelection()
          ) : (
            <RiskQuestionnaire
              capacityAnswers={this.state.capacityAnswers}
              willingnessAnswers={this.state.willingnessAnswers}
              capacityTotal={this.state.capacityTotal}
              willingnessTotal={this.state.willingnessTotal}
              combinedScore={this.state.combinedScore}
              riskCategory={this.state.riskCategory}
              selectedPortfolio={this.state.selectedPortfolio}
              portfolioOverride={this.state.portfolioOverride}
              overrideReason={this.state.overrideReason}
              overrideOtherText={this.state.overrideOtherText}
              riskToleranceOptions={this.getRiskToleranceOptions()}
              riskToleranceAliases={this.getRiskToleranceAliases()}
              strategySelected={!!this.state.strategy}
              strategy={this.state.strategy}
              onAnswerChange={this.handleRiskAnswerChange}
              onPortfolioChange={this.handlePortfolioChange}
              onOverrideToggle={this.handleOverrideToggle}
              onOverrideReasonChange={this.handleOverrideReasonChange}
              onOverrideOtherTextChange={this.handleOverrideOtherTextChange}
              onReset={this.handleRiskReset}
              riskChartUrl={this.state.riskChartUrl}
            />
          )}
        </CollapsibleSection>

        <CollapsibleSection
          sectionNumber="4"
          title="Fees"
          isExpanded={expandedSection === "4"}
          isValid={this.isSection4Valid()}
          onToggle={() => this.toggleSection("4")}
        >
          <ISPFeeSchedule
            strategy={this.state.strategy}
            lists={this.props.lists}
            bondType={this.state.bondType}
            registrations={this.isFusionMultiRegistration() ? [] : this.state.registrations}
            customFees={this.state.customFees}
            managementFees={this.state.managementFees}
            managementFeesLoading={this.state.managementFeesLoading}
            managementFeeTierOptions={this.state.availableManagementFeeTierOptions}
            isFusion={this.isFusionStrategy()}
            fusionRegistrationType={this.state.fusionRegistrationType}
            fusionAllocations={this.state.fusionAllocations}
            jointClients={this.state.jointClients}
            fusionAllocationLimitErrors={this.state.fusionAllocations.map((a) =>
              this.getFusionAllocationLimitError(a)
            )}
            fusionFeeBlocks={this.buildFusionFeeBlocks()}
            fusionSubFees={this.state.fusionSubFees}
            fusionSubFeesLoading={this.state.fusionSubFeesLoading}
            fusionFeeValidation={this.state.fusionFeeValidation}
            onFusionAllocationsChange={this.handleFusionAllocationsChange}
            hasPath={this.hasPath()}
            values={{
              iarFeeTier: this.state.iarFeeTier,
              selectedManagementFeeTierKey: this.state.selectedManagementFeeTierKey,
              iarFeeType: this.state.iarFeeType,
              iarFeeFlat: this.state.iarFeeFlat,
              iarFeeTiered: this.state.iarFeeTiered,
              iarFeeScheduleType: this.state.iarFeeScheduleType,
              iarFeeWaiver: this.state.iarFeeWaiver,
              pathIarFeeType: this.state.pathIarFeeType,
              pathIarFeeFlat: this.state.pathIarFeeFlat,
              pathIarFeeTiered: this.state.pathIarFeeTiered,
            }}
            onChange={this.handleFieldChange}
          />
        </CollapsibleSection>

        <CollapsibleSection
          sectionNumber="5"
          title="Review and Submit"
          isExpanded={expandedSection === "5"}
          isValid={this.isFormValid()}
          onToggle={() => this.toggleSection("5")}
        >
          <ISPConfirm
            state={{
              ...this.state,
              strategyDisplayName: this.getStrategyDisplayName(),
              pathStrategy: this.hasPathBondChoice()
                ? this.getPathStrategyName()
                : "",
              hasPath: this.hasPath(),
              fusionAllocationRiskValidation:
                this.getFusionAllocationRiskValidation(),
            }}
            lists={this.props.lists}
            isFormValid={this.isFormValid()}
            onSubmit={this.handleSubmit}
            isSubmitting={this.state.isSubmitting}
            submitError={this.state.submitError}
          />
        </CollapsibleSection>

        {/* Zero Fee Waiver Modal */}
        {this.state.showZeroFeeModal && (() => {
          const zeroFields = this.findZeroFees();
          const feeTypeLabel = this.state.iarFeeType === "Fixed Percentage" ? "Fixed Percentage" : "Tiered Fee Schedule";
          return (
            <div>
              <div className="modal-backdrop fade show" style={{ zIndex: 1050 }} />
              <div className="modal fade show" style={{ display: "block", zIndex: 1060 }} tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="zeroFeeModalTitle">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title" id="zeroFeeModalTitle" style={{ color: "#dc3545" }}>
                        0% Fee Detected — {feeTypeLabel}
                      </h5>
                    </div>
                    <div className="modal-body">
                      <p>
                        The following {zeroFields.length === 1 ? "rate is" : "rates are"} set
                        to <strong>0%</strong> under the <strong>{feeTypeLabel}</strong> structure,
                        which means the advisor fee will be waived for {zeroFields.length === 1 ? "this tier" : "these tiers"}:
                      </p>
                      <ul>
                        {zeroFields.map((field, i) => (
                          <li key={i}><strong>{field}</strong> — 0%</li>
                        ))}
                      </ul>
                      <p className="mb-0 text-muted" style={{ fontSize: "13px" }}>
                        By confirming, you acknowledge that a 0% IAR fee is intentional for this account.
                        This acknowledgment will be recorded with your email and timestamp.
                      </p>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={this.handleCancelZeroFee}
                      >
                        Go Back and Edit Fees
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={this.handleConfirmZeroFee}
                      >
                        Confirm 0% Fee Waiver
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Confirm Modal (delete advisor/client) */}
        {this.state.confirmModal && (
          <div>
            <div className="modal-backdrop fade show" style={{ zIndex: 1050 }} />
            <div className="modal fade show" style={{ display: "block", zIndex: 1060 }} tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog modal-dialog-centered modal-sm">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{this.state.confirmModal.title}</h5>
                  </div>
                  <div className="modal-body">
                    <p className="mb-0">{this.state.confirmModal.message}</p>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => this.setState({ confirmModal: null })}>
                      Cancel
                    </button>
                    <button type="button" className="btn btn-danger" onClick={this.state.confirmModal.onConfirm}>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Screen */}
        {this.state.showSuccess && (
          <div>
            <div
              className="modal-backdrop fade show"
              style={{ zIndex: 1050 }}
            />
            <div
              className="modal fade show"
              style={{ display: "block", zIndex: 1060 }}
              tabIndex="-1"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ispSuccessModalTitle"
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="ispSuccessModalTitle" style={{ color: "#385EA4" }}>
                      Investment Strategy Plan Submitted
                    </h5>
                  </div>
                  <div className="modal-body text-center py-4">
                    <div
                      style={{
                        fontSize: "48px",
                        color: "#28a745",
                        marginBottom: "10px",
                      }}
                    >
                      &#10003;
                    </div>
                    <h5>Your Investment Strategy Plan has been submitted.</h5>
                    <p className="text-muted">
                      {this.getStrategyDisplayName()} — {this.getCombinedClientName() || "Client"}
                    </p>
                    <p className="text-muted" style={{ fontSize: "0.9em" }}>
                      Documents are being generated — please allow ~30 seconds, then refresh on the submissions page.
                    </p>
                  </div>
                  <div className="modal-footer justify-content-center">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={this.handleCreateAnother}
                    >
                      Create Another ISP
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        if (this.props.updateProposalsState) {
                          this.props.updateProposalsState();
                        }
                        if (this.props.history) {
                          this.props.history.push("/submissions");
                        } else {
                          window.location.href = "/submissions";
                        }
                      }}
                    >
                      View Submissions
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default ISPForm;
